import React, { useState, useEffect } from 'react';
import '../quill-overrides.css';
import { get, post, put } from '../api';

const STATUS_COLORS = {
  draft: '#F59E0B', approved: '#3B82F6', scheduled: '#8B5CF6',
  sent: '#10B981', rejected: '#EF4444',
};

const MAX_IMAGE_ATTACHMENTS = 10;

function IconImage() {
  return <svg className="agent-action-icon" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="5" width="18" height="14" rx="2.5" />
    <circle cx="8.5" cy="10" r="1.5" />
    <path d="M21 16l-5.2-5.2a1.4 1.4 0 0 0-2 0L5 19" />
    <path d="M13.5 14.5l-2-2a1.4 1.4 0 0 0-2 0L4.5 17.5" />
  </svg>;
}

function IconMic() {
  return <svg className="agent-action-icon" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="3" width="6" height="11" rx="3" />
    <path d="M5 11a7 7 0 0 0 14 0" />
    <path d="M12 18v3" />
    <path d="M8 21h8" />
  </svg>;
}

function IconStop() {
  return <svg className="agent-action-icon" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
    <rect x="7" y="7" width="10" height="10" rx="1.8" />
  </svg>;
}

function IconSpinner() {
  return <svg className="agent-action-icon agent-action-spinner" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M12 3a9 9 0 1 1-8.2 5.3" />
  </svg>;
}

const GENERATE_DEFAULTS = {
  segment: 'full_list',
  contentTopic: '',
  triggerTopic: '',
  emailType: 'story',
  offerName: 'Roadmap Implementation Session',
  offerPrice: '29',
  offerNormalPrice: '297',
  offerScarcity: '25 spots only',
  offerDeadline: 'this week only',
  offerMainUrl: 'https://freedomincomeoptions.com/roadmap/',
  offerTriggerUrl: 'https://members.freedomincomeoptions.com/register/',
  offerAngle: 'Walk away with your complete trading roadmap built out — live session with Casey',
};

const DEFAULT_NEW_DRAFT = {
  subject: '', body: '', segment_target: ['full_list'], email_type: 'story', send_mode: 'broadcast', trigger_draft_id: '', buyer_level_target: [], source_broadcast_id: '',
};

const COMPOSE_MANUAL_CACHE_KEY = 'ecc.compose.manualDraft.v1';
const COMPOSE_AI_CACHE_KEY = 'ecc.compose.aiPrompt.v1';
const COMPOSE_GENERATOR_CACHE_KEY = 'ecc.compose.generator.v1';
const COMPOSE_EDIT_CACHE_PREFIX = 'ecc.compose.editDraft.v1.';

function canUseLocalStorage() {
  return typeof window !== 'undefined' && window.localStorage;
}

function readJsonCache(key, fallback) {
  if (!canUseLocalStorage()) return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    if (fallback && typeof fallback === 'object' && !Array.isArray(fallback) && parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return { ...fallback, ...parsed };
    }
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
}

function writeJsonCache(key, value) {
  if (!canUseLocalStorage()) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Best-effort browser cache only.
  }
}

function clearJsonCache(key) {
  if (!canUseLocalStorage()) return;
  try { window.localStorage.removeItem(key); } catch { /* best effort */ }
}


function stripHtmlToText(value = '') {
  return String(value || '')
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/\s+/g, ' ')
    .trim();
}

function getDraftSendBlockReason(draft = {}) {
  if (!String(draft.subject || '').trim()) return 'Add a subject before testing, scheduling, or approving this email.';
  if (!stripHtmlToText(draft.body)) return 'Add email body content before testing, scheduling, or approving this email.';
  return '';
}

function hasManualDraftContent(draft = {}) {
  return Boolean(
    String(draft.subject || '').trim()
    || String(draft.body || '').trim()
    || draft.trigger_draft_id
    || draft.source_broadcast_id
  );
}

/**
 * Detect plain-text and auto-convert to HTML.
 * Matches the backend ensureHtmlBody() logic so preview is accurate.
 */
function ensureHtmlBody(body) {
  if (!body || typeof body !== 'string') return body;
  if (/<(p|br|div|a\s)[>\s/]/i.test(body)) return body;

  let html = body.replace(
    /(https?:\/\/[^\s<>"']+)/g,
    '<a href="$1">$1</a>'
  );

  const paragraphs = html.split(/\n{2,}/);
  html = paragraphs
    .map(p => p.trim())
    .filter(p => p.length > 0)
    .map(p => `<p>${p.replace(/\n/g, '<br>')}</p>`)
    .join('\n\n');

  return html;
}

function addPurchaseExclusionsToQuery(qs, draft = {}) {
  const rawIds = draft.exclude_purchased_membership_ids;
  const values = Array.isArray(rawIds) ? rawIds : (rawIds == null ? [] : String(rawIds).split(','));
  const ids = [...new Set(values
    .map(value => String(value ?? '').trim())
    .filter(value => /^\d+$/.test(value))
    .map(Number)
    .filter(value => Number.isSafeInteger(value) && value > 0))];
  if (ids.length > 0) qs.set('exclude_purchased_membership_ids', ids.join(','));
}

function getDirectRecipients(draft = {}) {
  const raw = draft.recipient_emails;
  const values = Array.isArray(raw) ? raw : (raw ? String(raw).split(',') : []);
  return values.map(value => String(value || '').trim()).filter(Boolean);
}

// Direct-list drafts send only to their named addresses; the projection must
// count those addresses rather than the placeholder segment.
function getExcludedRecipients(draft = {}) {
  const raw = draft.excluded_recipient_emails;
  const values = Array.isArray(raw) ? raw : (raw ? String(raw).split(',') : []);
  return values.map(value => String(value || '').trim()).filter(Boolean);
}

function addDirectRecipientsToQuery(qs, draft = {}) {
  const recipients = getDirectRecipients(draft);
  if (recipients.length > 0) qs.set('recipient_emails', recipients.join(','));
  const excluded = getExcludedRecipients(draft);
  if (excluded.length > 0) qs.set('excluded_recipient_emails', excluded.join(','));
}

const SEGMENT_LABELS = {
  __direct_list__: 'Direct list',
  full_list: 'Full List',
  __active_last_30__: 'Active Last 30 Days',
  __clicked_last_7__: 'Clicked Last 7 Days',
  hot: 'Hot',
  warm: 'Warm',
  cool: 'Cool',
  cold: 'Cold',
  dead: 'Dead',
  full_list: 'Full List',
  'hot,warm': 'Hot + Warm',
  'warm,cool': 'Warm + Cool',
  'warm,cool,cold': 'Warm + Cool + Cold',
  'cool,cold': 'Cool + Cold',
};

const SEGMENT_OPTIONS = [
  { value: 'full_list', label: 'Full List (all)' },
  { value: '__active_last_30__', label: 'Active Last 30 Days' },
  { value: '__clicked_last_7__', label: 'Clicked Last 7 Days' },
  { value: 'hot', label: 'Hot' },
  { value: 'warm', label: 'Warm' },
  { value: 'cool', label: 'Cool' },
  { value: 'cold', label: 'Cold' },
  { value: 'dead', label: 'Dead' },
];

const AI_SEGMENT_OPTIONS = [
  { value: 'full_list', label: 'Full List', hint: 'Hot + Warm + Cool + Cold + Dead' },
  { value: 'hot', label: 'Hot', hint: 'Opened or clicked in 3 days' },
  { value: 'warm', label: 'Warm', hint: 'Clicked in 7 days' },
  { value: 'cool', label: 'Cool', hint: 'Opened in 14 days' },
  { value: 'cold', label: 'Cold', hint: 'No engagement in 30+ days' },
  { value: 'dead', label: 'Dead', hint: 'No engagement in 90+ days' },
];

const REAL_SEGMENT_VALUES = ['hot', 'warm', 'cool', 'cold', 'dead'];
const RESERVED_SEGMENT_VALUES = ['__active_last_30__', '__clicked_last_7__'];

// Normalize segment_target to array regardless of how API returns it
function normalizeSegmentArr(val) {
  if (!val) return ['full_list'];
  if (Array.isArray(val)) return val.length ? val : ['full_list'];
  if (typeof val === 'string') return val.split(',').map(s => s.trim()).filter(Boolean);
  return ['full_list'];
}

function segmentLabel(val) {
  if (!val) return 'Full List';
  // Handle array format from new DB schema
  const arr = Array.isArray(val)
    ? val
    : String(val).split(',').map(v => v.trim()).filter(Boolean);
  if (!arr.length || arr.includes('full_list')) return 'Full List';
  if (REAL_SEGMENT_VALUES.every(segment => arr.includes(segment))) return 'Full List';
  return arr.map(v => SEGMENT_LABELS[v] || v).join(' + ');
}

function toggleSegmentSelection(currentValue, value, checked) {
  const current = normalizeSegmentArr(currentValue);
  if (!checked) {
    const next = current.filter(segment => segment !== value);
    return next.length ? next : ['full_list'];
  }
  if (RESERVED_SEGMENT_VALUES.includes(value)) return [value];
  const withoutReserved = current.filter(segment => !RESERVED_SEGMENT_VALUES.includes(segment) && segment !== 'full_list' && segment !== value);
  return [...withoutReserved, value];
}

function isSegmentOptionDisabled(currentValue, value) {
  if (value === 'full_list') return false;
  const current = normalizeSegmentArr(currentValue);
  if (current.includes('full_list')) return true;
  const hasReserved = current.some(segment => RESERVED_SEGMENT_VALUES.includes(segment));
  const isReserved = RESERVED_SEGMENT_VALUES.includes(value);
  return isReserved ? current.some(segment => !RESERVED_SEGMENT_VALUES.includes(segment)) : hasReserved;
}

function buyerLevelLabel(val) {
  if (!val) return 'All buyer tiers';
  const arr = Array.isArray(val)
    ? val
    : String(val).split(',').map(v => v.trim()).filter(Boolean);
  if (!arr.length) return 'All buyer tiers';
  return arr.map(v => TIER_LABELS_AI[v] || v.replace(/_/g, '-')).join(' + ');
}

function hasLinkedTrigger(draft) {
  return Boolean(draft?.trigger_draft_id) || Number(draft?.linked_trigger_count || 0) > 0;
}

function linkedTriggerLabel(draft) {
  if (draft?.trigger_draft_id) return '⚡ trigger linked';
  const count = Number(draft?.linked_trigger_count || 0);
  if (count === 1) return '⚡ companion trigger linked';
  if (count > 1) return `⚡ ${count} companion triggers linked`;
  return '';
}

function effectiveStatus(draft) {
  if (draft?.status === 'approved' && draft.scheduled_at && new Date(draft.scheduled_at) > new Date()) {
    return 'scheduled';
  }
  return draft?.status || 'draft';
}

function formatScheduledAt(iso) {
  if (!iso) return '';
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(new Date(iso));
}

function formatCardDateTime(iso) {
  if (!iso) return '';
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(new Date(iso));
}

function getScheduleDisplay(draft) {
  if (!draft) return null;

  if (draft.send_mode === 'triggered') {
    return {
      label: 'Triggered — fires on click',
      color: '#8B5CF6',
      background: '#8B5CF622',
    };
  }

  if (draft.status === 'sent') {
    return {
      label: 'Sent',
      color: '#10B981',
      background: '#10B98122',
    };
  }

  if (draft.scheduled_at) {
    const scheduledDate = new Date(draft.scheduled_at);
    const isFuture = scheduledDate > new Date();
    return {
      label: `${isFuture ? 'Scheduled' : 'Queued / due now'}: ${formatScheduledAt(draft.scheduled_at)}`,
      color: isFuture ? '#A78BFA' : '#F59E0B',
      background: isFuture ? '#8B5CF622' : '#F59E0B22',
    };
  }

  if (draft.status === 'approved') {
    return {
      label: 'No scheduled time set — use Reschedule',
      color: '#EF4444',
      background: '#EF444422',
    };
  }

  return {
    label: 'Not scheduled yet',
    color: 'var(--muted)',
    background: 'transparent',
  };
}

function isNeedsReviewDraft(draft) {
  return draft?.status === 'draft'
    || (draft?.status === 'approved' && draft?.send_mode !== 'triggered' && !draft?.scheduled_at);
}

function isActiveDraft(draft) {
  return draft?.status === 'approved' && !isNeedsReviewDraft(draft);
}

function isLibraryDraft(draft) {
  return ['sent', 'rejected'].includes(draft?.status);
}

function recentSentDrafts(drafts, days = 7) {
  const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
  return drafts.filter(d => d.status === 'sent' && new Date(d.updated_at || d.created_at).getTime() >= cutoff);
}

function buildComposeStatus(drafts) {
  const needsReview = drafts.filter(isNeedsReviewDraft);
  const active = drafts.filter(isActiveDraft);
  const scheduled = active.filter(d => d.send_mode !== 'triggered' && d.scheduled_at && new Date(d.scheduled_at) > new Date());
  const queued = active.filter(d => d.send_mode !== 'triggered' && (!d.scheduled_at || new Date(d.scheduled_at) <= new Date()));
  const triggers = active.filter(d => d.send_mode === 'triggered');
  const recentSent = recentSentDrafts(drafts);

  return { needsReview, active, scheduled, queued, triggers, recentSent };
}

function sortDraftsForComposeTab(tab, list) {
  const time = (draft) => new Date(draft.scheduled_at || draft.updated_at || draft.created_at || 0).getTime();
  const sorted = [...list];
  if (tab === 'active') {
    return sorted.sort((a, b) => {
      const modeScore = (draft) => draft.send_mode === 'triggered' ? 2 : (draft.scheduled_at && new Date(draft.scheduled_at) > new Date() ? 0 : 1);
      return modeScore(a) - modeScore(b) || time(a) - time(b);
    });
  }
  return sorted.sort((a, b) => time(b) - time(a));
}

function StatusCard({ label, value, subline, tone }) {
  return (
    <div className={`compose-status-card ${tone || ''}`}>
      <div className="compose-status-label">{label}</div>
      <div className="compose-status-value">{value}</div>
      <div className="compose-status-subline">{subline}</div>
    </div>
  );
}

function EmptyComposeTab({ tab }) {
  const copy = {
    active: {
      title: 'Nothing active right now.',
      body: 'Approved broadcasts, scheduled sends, queued sends, and approved trigger drafts will show here.',
    },
    review: {
      title: 'All caught up.',
      body: 'Drafts that need Casey review or approval will show here.',
    },
    library: {
      title: 'No library items yet.',
      body: 'Sent and rejected drafts will live here as the archive.',
    },
  }[tab] || {};

  return (
    <div className="compose-empty-tab">
      <h3>{copy.title}</h3>
      <p>{copy.body}</p>
    </div>
  );
}

const TIER_BG = { non_buyer: '#71717A', low_tier: '#F59E0B', core: '#10B981' };
const TIER_LABELS_AI = { non_buyer: 'Non-Buyer', low_tier: 'Low-Tier', core: 'Core' };
const TIERS_ORDER = ['non_buyer', 'low_tier', 'core'];

const NUMBER_WORDS = {
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
};

function inferCampaignEmailCount(prompt = '') {
  return 1;
}

function campaignIntentLabel(prompt = '') {
  return '1 main email + click-triggered follow-up';
}

function campaignShapeLabel(count = 1) {
  return '1 main email + 1 click-triggered follow-up';
}

function normalizePriorTime(timePhrase = '') {
  const text = String(timePhrase).toLowerCase().replace(/\./g, '').replace(/\s+/g, ' ').trim();
  if (!text) return null;
  if (text.includes('noon')) return '12:00 PM ET';
  if (text.includes('midnight')) return '12:00 AM ET';

  const match = text.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)?$/i);
  if (!match) return null;

  const hour = Number(match[1]);
  if (hour < 1 || hour > 12) return null;
  const minutes = match[2] || '00';
  const suffix = match[3] ? match[3].toUpperCase() : (hour === 12 && minutes === '00' ? 'PM' : null);
  if (!suffix) return null;
  return `${hour}:${minutes.padStart(2, '0')} ${suffix} ET`;
}

function normalizeTimingDay(dayPhrase = '') {
  const day = String(dayPhrase).toLowerCase();
  if (day === 'today') return 'today';
  if (day === 'yesterday') return 'yesterday';
  if (day === 'tomorrow') return 'tomorrow';
  return day.charAt(0).toUpperCase() + day.slice(1);
}

function getPromptInstructionBlock(prompt = '') {
  const text = String(prompt || '');
  const bodyIndex = text.search(/^\s*BODY\s*:/im);
  return bodyIndex >= 0 ? text.slice(0, bodyIndex) : text;
}

function getExplicitTimingInstructionText(prompt = '') {
  const instructionBlock = getPromptInstructionBlock(prompt);
  const timingLines = instructionBlock
    .split(/\n+/)
    .map(line => line.trim())
    .filter(Boolean)
    .filter(line => /^(send|schedule|scheduled|date|time|timing)\s*:/i.test(line) || /^\s*(send|schedule)\b/i.test(line));

  return timingLines.join('\n');
}

function detectEmailTimingContexts(prompt = '') {
  const text = getExplicitTimingInstructionText(prompt);
  if (!text) return [];
  const timePattern = '(12\\s*noon|noon|midnight|\\d{1,2}(?::\\d{2})?\\s*(?:am|pm|a\\.m\\.|p\\.m\\.)?|12)';
  const dayPattern = '(today|yesterday|tomorrow|monday|tuesday|wednesday|thursday|friday|saturday|sunday)';
  const timezonePattern = '(?:\\s*(?:et|eastern|eastern\\s+time))?';
  const contexts = [];
  const seen = new Set();

  const addContext = (label, day, timePhrase, sourceText) => {
    const normalized = normalizePriorTime(timePhrase);
    if (!normalized) return;
    const value = day ? `${normalizeTimingDay(day)} at ${normalized}` : normalized;
    const key = value.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    contexts.push({ label, value, sourceText });
  };

  const sentEmailPattern = new RegExp(`\\bsent\\s+an?\\s+email\\b.{0,80}?\\b${dayPattern}?\\b.{0,50}?\\b(?:at\\s*)?${timePattern}${timezonePattern}\\b`, 'gi');
  let match;
  while ((match = sentEmailPattern.exec(text)) !== null) {
    addContext('Prior email', match[1] || 'today', match[2], match[0]);
  }

  const schedulePhrasePattern = new RegExp(`\\b(?:schedule|send)\\b.{0,70}?\\b(?:for\\s+)?${timePattern}${timezonePattern}.{0,35}?\\b${dayPattern}\\b`, 'gi');
  while ((match = schedulePhrasePattern.exec(text)) !== null) {
    addContext('Intended send', match[2], match[1], match[0]);
  }

  const timeThenDayPattern = new RegExp(`\\b${timePattern}${timezonePattern}.{0,35}?\\b${dayPattern}\\b`, 'gi');
  while ((match = timeThenDayPattern.exec(text)) !== null) {
    addContext('Intended send', match[2], match[1], match[0]);
  }

  const dayThenTimePattern = new RegExp(`\\b${dayPattern}\\b.{0,70}?\\bat\\s*${timePattern}${timezonePattern}\\b`, 'gi');
  while ((match = dayThenTimePattern.exec(text)) !== null) {
    addContext('Intended send', match[1], match[2], match[0]);
  }

  return contexts.slice(0, 4);
}

function detectPriorEmailContext(prompt = '') {
  const contexts = detectEmailTimingContexts(prompt);
  return contexts[0]?.value || null;
}

function getDraftTimingContexts(draft) {
  const prompt = draft?.ai_campaign_prompt || draft?.campaign_prompt || '';
  if (!prompt) return [];
  return detectEmailTimingContexts(prompt);
}

function getDraftTimingLabel(draft) {
  const contexts = getDraftTimingContexts(draft);
  if (!contexts.length) return null;
  const primary = contexts[0];
  const prefix = draft?.send_mode === 'triggered' ? 'Context: follows' : 'Context:';
  return `${prefix} ${primary.label.toLowerCase()} ${primary.value}`;
}

function formatApproxCount(count) {
  if (typeof count !== 'number' || Number.isNaN(count)) return 'Unknown';
  return `approx. ${count.toLocaleString()} contacts`;
}

function buildAIGenerationConfirmation({ prompt, segments, buyerLevels, imageUrls, audienceEstimate = null }) {
  const segmentValues = segments.includes('full_list') ? ['full_list'] : segments;
  const emailCount = inferCampaignEmailCount(prompt);
  const timingContexts = detectEmailTimingContexts(prompt);
  return {
    prompt,
    campaign: campaignShapeLabel(emailCount),
    emailCount,
    priorEmailContext: timingContexts[0]?.value || null,
    timingContexts,
    timingMode: timingContexts.length ? 'detected' : 'none',
    timingText: timingContexts[0]?.value || '',
    selectedSegments: segmentValues,
    selectedBuyerLevels: buyerLevels,
    lists: segmentLabel(segmentValues),
    triggerAudience: buyerLevelLabel(buyerLevels),
    images: imageUrls.length,
    audienceEstimate,
    audienceLoading: audienceEstimate === null,
    audienceError: null,
  };
}

export default function Drafts() {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composeTab, setComposeTab] = useState('review');

  // Panels
  const [showCreate, setShowCreate] = useState(() => hasManualDraftContent(readJsonCache(COMPOSE_MANUAL_CACHE_KEY, DEFAULT_NEW_DRAFT)));
  const [showGenerate, setShowGenerate] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // ── AI Writing Agent state ──────────────────────────────────────────────
  const [showAIAgent, setShowAIAgent] = useState(true);
  const aiCached = readJsonCache(COMPOSE_AI_CACHE_KEY, { prompt: '', imageUrls: [], segmentTarget: [], buyerLevelTarget: [] });
  // 'prompt' | 'review' | 'approved'
  const [aiAgentState, setAIAgentState] = useState(() => aiCached.agentState || 'prompt');
  const [aiPrompt, setAIPrompt] = useState(() => aiCached.prompt || '');
  const [aiImageUrls, setAIImageUrls] = useState(() => Array.isArray(aiCached.imageUrls) ? aiCached.imageUrls : []);
  const [aiImageUploading, setAIImageUploading] = useState(false);
  const [aiGenerating, setAIGenerating] = useState(false);
  const [aiCampaignId, setAICampaignId] = useState(() => aiCached.campaignId || null);
  const [aiEmails, setAIEmails] = useState(() => Array.isArray(aiCached.emails) ? aiCached.emails : []);
  const [aiCampaignSummary, setAICampaignSummary] = useState(() => aiCached.campaignSummary || '');
  const [aiDefaults, setAIDefaults] = useState(() => aiCached.defaults || null);
  const [aiClarificationQuestion, setAIClarificationQuestion] = useState(null);
  const [aiClarificationAnswer, setAIClarificationAnswer] = useState('');
  const [aiFeedback, setAIFeedback] = useState('');
  const [aiRevising, setAIRevising] = useState(false);
  const [aiMessages, setAIMessages] = useState(() => Array.isArray(aiCached.messages) ? aiCached.messages : []); // { role, content }
  const [aiApproving, setAIApproving] = useState(false);
  const [aiError, setAIError] = useState(null);
  const [aiMediaNotice, setAIMediaNotice] = useState(null);
  const [aiGenerationConfirm, setAIGenerationConfirm] = useState(null);
  const [aiTargetError, setAITargetError] = useState(null);
  const [aiSegmentTarget, setAISegmentTarget] = useState(() => Array.isArray(aiCached.segmentTarget) ? aiCached.segmentTarget : []);
  const [aiBuyerLevelTarget, setAIBuyerLevelTarget] = useState(() => Array.isArray(aiCached.buyerLevelTarget) ? aiCached.buyerLevelTarget : []);
  const [aiApprovedResult, setAIApprovedResult] = useState(null);
  const [aiRecording, setAIRecording] = useState(false);
  const [aiMediaRecorder, setAIMediaRecorder] = useState(null);
  // Per-email UI state: expanded trigger sections, body edit mode, variant pickers, active tier tab
  const [emailExpandedTrigger, setEmailExpandedTrigger] = useState({});
  const [emailBodyEditMode, setEmailBodyEditMode] = useState({});
  const [triggerBodyEditMode, setTriggerBodyEditMode] = useState({});
  const [activeTierTab, setActiveTierTab] = useState({}); // keyed by email_number, value: 'non_buyer'|'low_tier'|'core'

  // Manual create form
  const [newDraft, setNewDraft] = useState(() => readJsonCache(COMPOSE_MANUAL_CACHE_KEY, DEFAULT_NEW_DRAFT));
  const [approveWarning, setApproveWarning] = useState(null); // { id, subject, hasNoTrigger, campaignSchedule? }

  // Natural-language schedule modal
  const [scheduleModal, setScheduleModal] = useState(null); // { id, subject }
  const [scheduleText, setScheduleText] = useState('6 am tomorrow');
  const [schedulePreview, setSchedulePreview] = useState(null);
  const [scheduleError, setScheduleError] = useState(null);
  const [scheduleParsing, setScheduleParsing] = useState(false);
  const [scheduleSaving, setScheduleSaving] = useState(false);

  // "Send Now" confirmation modal — sends an approved, non-triggered broadcast
  // immediately via POST /api/drafts/:id/send-now instead of waiting for the
  // 5-minute send-emails cron.
  const [sendNowModal, setSendNowModal] = useState(null); // { id, subject, segmentLabel, buyerLevelLabel, count, countLoading, countError }
  const [sendNowInFlight, setSendNowInFlight] = useState(false);

  // Edit state
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ subject: '', body: '', trigger_draft_id: '', send_mode: 'broadcast', buyer_level_target: [], segment_target: ['full_list'] });
  const [editPreview, setEditPreview] = useState(false);
  const [editImageUploading, setEditImageUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  // AI generate form
  const [gen, setGen] = useState(() => readJsonCache(COMPOSE_GENERATOR_CACHE_KEY, GENERATE_DEFAULTS));
  const [generating, setGenerating] = useState(false);
  const [generateResult, setGenerateResult] = useState(null);

  const [message, setMessage] = useState('');
  const [projectedCounts, setProjectedCounts] = useState({}); // keyed by draft id

  const loadDrafts = () => {
    setLoading(true);
    return get('/api/drafts?limit=200')
      .then(d => {
        const loaded = d.drafts || [];
        setDrafts(loaded);
        setLoading(false);
        // Fetch projected recipient counts for unsent drafts in parallel
        const unsent = loaded.filter(dr => dr.status !== 'sent' && dr.status !== 'rejected');
        if (unsent.length > 0) {
          Promise.all(
            unsent.map(dr => {
              const qs = new URLSearchParams();
              // segment_target is now text[] — serialize as comma-separated for query param
              const segArr = normalizeSegmentArr(dr.segment_target);
              if (segArr.length && !segArr.includes('full_list')) {
                qs.set('segment_target', segArr.join(','));
              }
              if (dr.buyer_level_target && dr.buyer_level_target.length > 0) {
                const blt = Array.isArray(dr.buyer_level_target) ? dr.buyer_level_target.join(',') : dr.buyer_level_target;
                qs.set('buyer_level_target', blt);
              }
              addPurchaseExclusionsToQuery(qs, dr);
              addDirectRecipientsToQuery(qs, dr);
              return get(`/api/analytics/projected-recipients?${qs.toString()}`)
                .then(r => ({ id: dr.id, count: r.count }))
                .catch(() => ({ id: dr.id, count: null }));
            })
          ).then(results => {
            const map = {};
            results.forEach(r => { map[r.id] = r.count; });
            setProjectedCounts(map);
          });
        }
        return loaded;
      })
      .catch(() => {
        setLoading(false);
        return [];
      });
  };

  useEffect(() => { loadDrafts(); }, []);

  useEffect(() => {
    if (hasManualDraftContent(newDraft)) writeJsonCache(COMPOSE_MANUAL_CACHE_KEY, newDraft);
    else clearJsonCache(COMPOSE_MANUAL_CACHE_KEY);
  }, [newDraft]);

  useEffect(() => {
    if (aiPrompt.trim() || aiImageUrls.length || aiSegmentTarget.length || aiBuyerLevelTarget.length || aiEmails.length || aiCampaignId) {
      writeJsonCache(COMPOSE_AI_CACHE_KEY, {
        prompt: aiPrompt,
        imageUrls: aiImageUrls,
        segmentTarget: aiSegmentTarget,
        buyerLevelTarget: aiBuyerLevelTarget,
        agentState: aiAgentState,
        campaignId: aiCampaignId,
        emails: aiEmails,
        campaignSummary: aiCampaignSummary,
        defaults: aiDefaults,
        messages: aiMessages,
      });
    } else {
      clearJsonCache(COMPOSE_AI_CACHE_KEY);
    }
  }, [aiPrompt, aiImageUrls, aiSegmentTarget, aiBuyerLevelTarget, aiAgentState, aiCampaignId, aiEmails, aiCampaignSummary, aiDefaults, aiMessages]);

  useEffect(() => {
    const hasGeneratorContent = ['contentTopic', 'triggerTopic'].some(key => String(gen[key] || '').trim())
      || gen.offerName !== GENERATE_DEFAULTS.offerName
      || gen.offerMainUrl !== GENERATE_DEFAULTS.offerMainUrl
      || gen.offerTriggerUrl !== GENERATE_DEFAULTS.offerTriggerUrl;
    if (hasGeneratorContent) writeJsonCache(COMPOSE_GENERATOR_CACHE_KEY, gen);
    else clearJsonCache(COMPOSE_GENERATOR_CACHE_KEY);
  }, [gen]);

  useEffect(() => {
    if (!editingId) return;
    writeJsonCache(`${COMPOSE_EDIT_CACHE_PREFIX}${editingId}`, editDraft);
  }, [editingId, editDraft]);

  useEffect(() => {
    if (!scheduleModal) return;
    const text = scheduleText.trim();
    setSchedulePreview(null);
    setScheduleError(null);

    if (!text) return;

    const timeout = setTimeout(async () => {
      setScheduleParsing(true);
      try {
        const parsed = await post('/api/drafts/parse-schedule', { schedule_text: text });
        setSchedulePreview(parsed);
        setScheduleError(null);
      } catch (err) {
        setSchedulePreview(null);
        setScheduleError(err.message);
      } finally {
        setScheduleParsing(false);
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [scheduleModal, scheduleText]);

  // Triggered drafts available to link (approved or draft, send_mode=triggered)
  const triggerDrafts = drafts.filter(d => d.send_mode === 'triggered' && ['draft', 'approved'].includes(d.status));
  const composeStatus = buildComposeStatus(drafts);
  const activeDrafts = sortDraftsForComposeTab('active', composeStatus.active);
  const reviewDrafts = sortDraftsForComposeTab('review', composeStatus.needsReview);
  const libraryDrafts = sortDraftsForComposeTab('library', drafts.filter(isLibraryDraft));
  const visibleDrafts = composeTab === 'active'
    ? activeDrafts
    : composeTab === 'review'
      ? reviewDrafts
      : libraryDrafts;
  const composeTabs = [
    { key: 'active', label: 'Active', count: activeDrafts.length },
    { key: 'review', label: 'Needs Review', count: reviewDrafts.length },
    { key: 'library', label: 'Library', count: libraryDrafts.length },
  ];

  const handleAction = async (id, action) => {
    const draft = drafts.find(d => d.id === id);
    if (['approve', 'send-test'].includes(action)) {
      const blockReason = getDraftSendBlockReason(draft);
      if (blockReason) {
        setMessage(blockReason);
        setTimeout(() => setMessage(''), 5000);
        return;
      }
    }
    // Always confirm before approving — show modal for all cases
    if (action === 'approve') {
      // Fetch approve preview to check for campaign schedule
      let campaignSchedule = null;
      try {
        const preview = await get(`/api/drafts/${id}/approve-preview`);
        if (preview.mode === 'scheduled') {
          campaignSchedule = { scheduled_at: preview.scheduled_at, label: preview.label };
        }
      } catch (e) { /* ignore — fall back to immediate */ }
      setApproveWarning({
        id,
        subject: draft?.subject || '',
        hasNoTrigger: draft && draft.send_mode === 'broadcast' && !draft.trigger_draft_id,
        campaignSchedule,
      });
      return;
    }
    try {
      if (action === 'reject') await post(`/api/drafts/${id}/reject`, {});
      else if (action === 'send-test') {
        const res = await post(`/api/drafts/${id}/send-test`, {});
        setMessage(res.success ? 'Test email sent!' : `Failed: ${res.error}`);
        setTimeout(() => setMessage(''), 4000);
        return;
      }
      loadDrafts();
    } catch (err) { setMessage(`Error: ${err.message}`); }
  };

  const handleApproveComplete = async (approvedDraft = {}, fallbackSchedule = null) => {
    const isTriggered = approvedDraft.send_mode === 'triggered';
    const scheduleLabel = fallbackSchedule?.label || (approvedDraft.scheduled_at ? formatScheduledAt(approvedDraft.scheduled_at) : null);

    const loadedDrafts = await loadDrafts();
    const remainingReviewCount = buildComposeStatus(loadedDrafts).needsReview.length;

    if (composeTab === 'review' && remainingReviewCount > 0) {
      setComposeTab('review');
      setMessage(`Approved. ${remainingReviewCount} draft${remainingReviewCount === 1 ? '' : 's'} still need review.`);
      setTimeout(() => setMessage(''), 7000);
      return;
    }

    if (approvedDraft.status === 'sent') {
      setComposeTab('library');
      setMessage('Approved and sent. It moved to Library.');
    } else {
      setComposeTab('active');
      if (isTriggered) {
        setMessage('Approved. It moved to Active Triggers and will fire on clicks.');
      } else if (approvedDraft.schedule_source === 'campaign' || fallbackSchedule) {
        setMessage(`Approved & scheduled${scheduleLabel ? ` for ${scheduleLabel}` : ''}. It moved to Active.`);
      } else {
        setMessage('Approved. It moved to Active → Queued and will send within 5 minutes.');
      }
    }

    setTimeout(() => setMessage(''), 7000);
  };

  const openScheduleModal = (draft) => {
    const blockReason = getDraftSendBlockReason(draft);
    if (blockReason) {
      setMessage(blockReason);
      setTimeout(() => setMessage(''), 5000);
      return;
    }
    setScheduleModal({ id: draft.id, subject: draft.subject });
    setScheduleText('6 am tomorrow');
    setSchedulePreview(null);
    setScheduleError(null);
  };

  const handleScheduleConfirm = async () => {
    if (!scheduleModal || !scheduleText.trim()) return;
    setScheduleSaving(true);
    try {
      const scheduled = await post(`/api/drafts/${scheduleModal.id}/schedule`, { schedule_text: scheduleText.trim() });
      setScheduleModal(null);
      setComposeTab('active');
      setMessage(`Approved & scheduled for ${scheduled.schedule_label || formatScheduledAt(scheduled.scheduled_at)}. It moved to Active → Scheduled.`);
      setTimeout(() => setMessage(''), 7000);
      loadDrafts();
    } catch (err) {
      setScheduleError(err.message);
    } finally {
      setScheduleSaving(false);
    }
  };

  const openSendNowModal = async (draft) => {
    const blockReason = getDraftSendBlockReason(draft);
    if (blockReason) {
      setMessage(blockReason);
      setTimeout(() => setMessage(''), 5000);
      return;
    }
    const segArr = normalizeSegmentArr(draft.segment_target);
    const modal = {
      id: draft.id,
      subject: draft.subject,
      segmentLabel: segmentLabel(segArr),
      buyerLevelLabel: buyerLevelLabel(draft.buyer_level_target),
      directRecipients: getDirectRecipients(draft),
      excludedRecipients: getExcludedRecipients(draft),
      count: null,
      countLoading: true,
      countError: null,
    };
    setSendNowModal(modal);

    // Fetch a fresh (not cached) projected recipient count right before showing
    // the confirmation, so Casey sees an up-to-date number, not the count from
    // whenever the Compose list last loaded.
    try {
      const qs = new URLSearchParams();
      if (segArr.length && !segArr.includes('full_list')) qs.set('segment_target', segArr.join(','));
      if (draft.buyer_level_target && draft.buyer_level_target.length > 0) {
        const blt = Array.isArray(draft.buyer_level_target) ? draft.buyer_level_target.join(',') : draft.buyer_level_target;
        qs.set('buyer_level_target', blt);
      }
      addPurchaseExclusionsToQuery(qs, draft);
      addDirectRecipientsToQuery(qs, draft);
      const result = await get(`/api/analytics/projected-recipients${qs.toString() ? `?${qs.toString()}` : ''}`);
      const count = typeof result.count === 'number' ? result.count : Number(result.count);
      setSendNowModal(current => (current && current.id === draft.id) ? { ...current, count, countLoading: false } : current);
    } catch (err) {
      setSendNowModal(current => (current && current.id === draft.id)
        ? { ...current, countLoading: false, countError: err.message || 'Could not estimate audience' }
        : current);
    }
  };

  const handleSendNowConfirm = async () => {
    if (!sendNowModal || sendNowInFlight) return;
    const { id, subject } = sendNowModal;
    setSendNowInFlight(true);
    try {
      const result = await post(`/api/drafts/${id}/send-now`, {});
      setSendNowModal(null);
      const summary = result.outcomeSummary || {};
      setMessage(
        result.noEligibleRecipients
          ? `Sent now: no eligible recipients remained for "${subject}" — nothing was sent.`
          : `Send complete: "${subject}" — ${Number(summary.acceptedCount || 0).toLocaleString()} accepted, ${Number(summary.rejectedCount || 0).toLocaleString()} rejected, ${Number(summary.unknownCount || 0).toLocaleString()} unknown${result.remaining ? `; ${Number(result.remaining).toLocaleString()} unattempted remain` : ''}.`
      );
      setTimeout(() => setMessage(''), 8000);
      loadDrafts();
    } catch (err) {
      // Leave the modal open so Casey can see the error against the same context
      setSendNowModal(current => current ? { ...current, sendError: err.message } : current);
    } finally {
      setSendNowInFlight(false);
    }
  };

  const handleCreate = async () => {
    const blockReason = getDraftSendBlockReason(newDraft);
    if (blockReason) {
      setMessage(blockReason);
      setTimeout(() => setMessage(''), 5000);
      return;
    }
    try {
      const payload = { ...newDraft };
      if (!payload.trigger_draft_id) delete payload.trigger_draft_id;
      if (!payload.source_broadcast_id) delete payload.source_broadcast_id;
      // buyer_level_target: send as array (empty array = null = all audiences)
      if (!payload.buyer_level_target || payload.buyer_level_target.length === 0) {
        payload.buyer_level_target = null;
      }
      await post('/api/drafts', payload);
      clearJsonCache(COMPOSE_MANUAL_CACHE_KEY);
      setShowCreate(false);
      setShowPreview(false);
      setNewDraft(DEFAULT_NEW_DRAFT);
      loadDrafts();
    } catch (err) { setMessage(`Error: ${err.message}`); }
  };

  const handleEdit = (draft) => {
    const cachedEdit = readJsonCache(`${COMPOSE_EDIT_CACHE_PREFIX}${draft.id}`, null);
    setEditingId(draft.id);
    setEditDraft({
      subject: cachedEdit?.subject ?? draft.subject,
      body: cachedEdit?.body ?? draft.body,
      trigger_draft_id: cachedEdit?.trigger_draft_id ?? (draft.trigger_draft_id || ''),
      send_mode: cachedEdit?.send_mode ?? (draft.send_mode || 'broadcast'),
      buyer_level_target: cachedEdit?.buyer_level_target ?? (Array.isArray(draft.buyer_level_target) ? draft.buyer_level_target : (draft.buyer_level_target ? [draft.buyer_level_target] : [])),
      segment_target: cachedEdit?.segment_target ?? normalizeSegmentArr(draft.segment_target),
      source_broadcast_id: cachedEdit?.source_broadcast_id ?? (draft.source_broadcast_id || ''),
    });
    setEditPreview(false);
    loadDrafts(); // refresh so newly-created trigger drafts appear in the dropdown
  };

  const uploadDraftImage = async (file) => {
    if (!file || !file.type?.startsWith('image/')) return null;
    const fd = new FormData();
    fd.append('image', file);
    const res = await fetch('/api/upload-image', { method: 'POST', body: fd });
    const data = await res.json();
    if (!res.ok || !data.url) throw new Error(data.error || 'Image upload failed');
    return data.url;
  };

  const imageTagForUrl = (url) => `<img src="${url}" alt="" style="max-width:400px;width:100%;height:auto;display:block;margin:12px 0;" />`;

  const shouldReplaceExistingDraftImages = (html = '') => {
    const imageMatches = String(html).match(/<img\b[^>]*>/gi) || [];
    return /<img\b[^>]*src=["']data:image\//i.test(html) || imageMatches.length === 1;
  };

  const insertUploadedDraftImage = (url) => {
    const imgTag = imageTagForUrl(url);

    setEditDraft(prev => {
      const currentBody = prev.body || '';

      // If the draft already has one image, treat the new upload/paste as a replacement.
      // This prevents stale/wrong images from surviving a manual edit and appearing in test sends.
      if (shouldReplaceExistingDraftImages(currentBody)) {
        return { ...prev, body: currentBody.replace(/<img\b[^>]*>/gi, imgTag) };
      }

      return { ...prev, body: `${currentBody}${imgTag}` };
    });
  };

  const handleEditImageFiles = async (fileList, event) => {
    const file = Array.from(fileList || []).find(f => f?.type?.startsWith('image/'));
    if (!file) return;
    event?.preventDefault?.();
    event?.stopPropagation?.();
    setEditImageUploading(true);
    try {
      const url = await uploadDraftImage(file);
      insertUploadedDraftImage(url);
      setMessage('Image uploaded and inserted into draft. Save changes before sending a test.');
      setTimeout(() => setMessage(''), 5000);
    } catch (err) {
      setMessage(`Image upload failed: ${err.message}`);
      setTimeout(() => setMessage(''), 5000);
    } finally {
      setEditImageUploading(false);
    }
  };

  const handleEditBodyPaste = (event) => {
    const items = Array.from(event.clipboardData?.items || []);
    const imageFiles = items
      .filter(item => item.kind === 'file' && item.type.startsWith('image/'))
      .map(item => item.getAsFile())
      .filter(Boolean);
    if (imageFiles.length) handleEditImageFiles(imageFiles, event);
  };

  const handleEditBodyDrop = (event) => {
    const imageFiles = Array.from(event.dataTransfer?.files || []).filter(file => file.type?.startsWith('image/'));
    if (imageFiles.length) handleEditImageFiles(imageFiles, event);
  };

  const handleSaveEdit = async () => {
    const blockReason = getDraftSendBlockReason(editDraft);
    if (blockReason) {
      setMessage(blockReason);
      setTimeout(() => setMessage(''), 5000);
      return;
    }
    setSaving(true);
    try {
      const payload = { ...editDraft };
      if (!payload.trigger_draft_id) payload.trigger_draft_id = null;
      if (!payload.source_broadcast_id) payload.source_broadcast_id = null;
      // Normalize buyer_level_target: empty array → null (all audiences)
      if (!payload.buyer_level_target || (Array.isArray(payload.buyer_level_target) && payload.buyer_level_target.length === 0)) {
        payload.buyer_level_target = null;
      }
      await put(`/api/drafts/${editingId}`, payload);
      clearJsonCache(`${COMPOSE_EDIT_CACHE_PREFIX}${editingId}`);
      setEditingId(null);
      setEditDraft({ subject: '', body: '', trigger_draft_id: '', send_mode: 'broadcast', buyer_level_target: [], source_broadcast_id: '', segment_target: ['full_list'] });
      setEditPreview(false);
      loadDrafts();
      setMessage('Draft updated.');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage(`Error: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleGenerate = async () => {
    if (!gen.contentTopic.trim()) {
      setMessage('Content topic is required.');
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    setGenerating(true);
    setGenerateResult(null);
    try {
      const result = await post('/api/drafts/generate', {
        segment: gen.segment,
        contentTopic: gen.contentTopic,
        triggerTopic: gen.triggerTopic || undefined,
        emailType: gen.emailType,
        mainUrl: gen.offerMainUrl,
        triggerUrl: gen.offerTriggerUrl,
        weeklyOffer: {
          name: gen.offerName,
          price: parseFloat(gen.offerPrice),
          normalPrice: parseFloat(gen.offerNormalPrice) || null,
          scarcity: gen.offerScarcity,
          deadline: gen.offerDeadline,
          mainUrl: gen.offerMainUrl,
          triggerUrl: gen.offerTriggerUrl,
          angle: gen.offerAngle,
        },
      });
      setGenerateResult(result);
      loadDrafts();
      setMessage(`✓ Generated: "${result.main.subject}" + trigger draft — both saved as drafts.`);
      setTimeout(() => setMessage(''), 6000);
    } catch (err) {
      setMessage(`Generation failed: ${err.message}`);
    } finally {
      setGenerating(false);
    }
  };

  // ── AI Agent handlers ───────────────────────────────────────────────────

  const uploadAIImageFiles = async (fileList, source = 'upload') => {
    const files = Array.from(fileList || []).filter(file => file && file.type && file.type.startsWith('image/'));
    if (!files.length) return;

    const openSlots = MAX_IMAGE_ATTACHMENTS - aiImageUrls.length;
    if (openSlots <= 0) {
      setAIError(`Max ${MAX_IMAGE_ATTACHMENTS} images per prompt.`);
      setAIMediaNotice(null);
      return;
    }

    const selectedFiles = files.slice(0, openSlots);
    const wasCapped = files.length > openSlots;
    setAIImageUploading(true);
    setAIError(null);
    setAIMediaNotice(wasCapped ? `Max ${MAX_IMAGE_ATTACHMENTS} images per prompt. Uploading the first ${openSlots}.` : null);

    const fd = new FormData();
    selectedFiles.forEach(file => fd.append('images', file));

    try {
      const res = await fetch('/api/upload-images', { method: 'POST', body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Image upload failed');

      const urls = Array.isArray(data.urls) ? data.urls : (data.url ? [data.url] : []);
      if (!urls.length) throw new Error('Image upload failed');
      setAIImageUrls(prev => [...prev, ...urls].slice(0, MAX_IMAGE_ATTACHMENTS));

      if (!wasCapped) {
        const noun = urls.length === 1 ? 'image' : 'images';
        setAIMediaNotice(source === 'paste' ? `Pasted ${noun} attached.` : source === 'drop' ? `Dropped ${noun} attached.` : `${urls.length} ${noun} attached.`);
        setTimeout(() => setAIMediaNotice(null), 3500);
      }
    } catch (err) {
      setAIError(`Image upload failed: ${err.message}`);
      setAIMediaNotice(null);
    } finally {
      setAIImageUploading(false);
    }
  };

  const handleAIPromptPaste = (event) => {
    const items = Array.from(event.clipboardData?.items || []);
    const imageFiles = items
      .filter(item => item.kind === 'file' && item.type.startsWith('image/'))
      .map(item => item.getAsFile())
      .filter(Boolean);
    if (!imageFiles.length) return;
    event.preventDefault();
    uploadAIImageFiles(imageFiles, 'paste');
  };

  const handleAIPromptDrop = (event) => {
    const files = Array.from(event.dataTransfer?.files || []).filter(file => file.type?.startsWith('image/'));
    if (!files.length) return;
    event.preventDefault();
    uploadAIImageFiles(files, 'drop');
  };

  const removeAIImage = (index) => {
    setAIImageUrls(prev => prev.filter((_, imageIndex) => imageIndex !== index));
  };

  const fetchAIAudienceEstimate = async (segments, buyerLevels) => {
    const qs = new URLSearchParams();
    const selectedSegments = segments.includes('full_list') ? [] : segments;
    if (selectedSegments.length) qs.set('segment_target', selectedSegments.join(','));
    if (buyerLevels.length) qs.set('buyer_level_target', buyerLevels.join(','));
    const query = qs.toString();
    const result = await get(`/api/analytics/projected-recipients${query ? `?${query}` : ''}`);
    return typeof result.count === 'number' ? result.count : Number(result.count);
  };

  const refreshAIGenerationAudienceEstimate = async (segments, buyerLevels) => {
    setAIGenerationConfirm(current => current
      ? { ...current, audienceLoading: true, audienceError: null }
      : current
    );
    try {
      const count = await fetchAIAudienceEstimate(segments, buyerLevels);
      setAIGenerationConfirm(current => current
        ? { ...current, audienceEstimate: count, audienceLoading: false, audienceError: null }
        : current
      );
    } catch (err) {
      setAIGenerationConfirm(current => current
        ? { ...current, audienceEstimate: null, audienceLoading: false, audienceError: err.message || 'Could not estimate audience' }
        : current
      );
    }
  };

  const updateAIGenerationSegments = (segments) => {
    const normalized = segments.includes('full_list') ? ['full_list'] : segments;
    setAIGenerationConfirm(current => current
      ? { ...current, selectedSegments: normalized, lists: segmentLabel(normalized) }
      : current
    );
    const buyerLevels = aiGenerationConfirm?.selectedBuyerLevels || [];
    refreshAIGenerationAudienceEstimate(normalized, buyerLevels);
  };

  const updateAIGenerationBuyerLevels = (buyerLevels) => {
    setAIGenerationConfirm(current => current
      ? { ...current, selectedBuyerLevels: buyerLevels, triggerAudience: buyerLevelLabel(buyerLevels) }
      : current
    );
    const segments = aiGenerationConfirm?.selectedSegments || ['full_list'];
    refreshAIGenerationAudienceEstimate(segments, buyerLevels);
  };

  const buildConfirmedPrompt = (confirmation) => {
    if (!confirmation) return aiPrompt;
    const timingLine = ['custom', 'detected'].includes(confirmation.timingMode) && confirmation.timingText?.trim()
      ? `Confirmed send timing/context: ${confirmation.timingText.trim()}`
      : 'Confirmed send timing/context: none provided; do not infer schedule from email body copy.';
    return `ECC CONFIRMED SETUP:\n- Campaign shape: ${campaignShapeLabel(confirmation.emailCount)}\n- ${timingLine}\n- Lists: ${segmentLabel(confirmation.selectedSegments)}\n- Trigger audience: ${buyerLevelLabel(confirmation.selectedBuyerLevels)}\n\nUSER PROMPT:\n${confirmation.prompt}`;
  };

  const handleAIGenerate = async (promptOverride) => {
    const finalPrompt = promptOverride || aiPrompt;
    if (!finalPrompt.trim()) return;
    if (aiSegmentTarget.length === 0) {
      setAITargetError('Select at least one segment.');
      return;
    }
    setAIError(null);
    setAITargetError(null);
    setAIClarificationQuestion(null);
    const baseConfirmation = buildAIGenerationConfirmation({
      prompt: finalPrompt,
      segments: aiSegmentTarget,
      buyerLevels: aiBuyerLevelTarget,
      imageUrls: aiImageUrls,
    });
    setAIGenerationConfirm(baseConfirmation);

    try {
      const count = await fetchAIAudienceEstimate(aiSegmentTarget, aiBuyerLevelTarget);
      setAIGenerationConfirm(current => current?.prompt === finalPrompt
        ? { ...current, audienceEstimate: count, audienceLoading: false, audienceError: null }
        : current
      );
    } catch (err) {
      setAIGenerationConfirm(current => current?.prompt === finalPrompt
        ? { ...current, audienceEstimate: null, audienceLoading: false, audienceError: err.message || 'Could not estimate audience' }
        : current
      );
    }
  };

  const executeAIGeneration = async (finalPrompt, overrides = {}) => {
    setAIGenerating(true);
    setAIError(null);
    setAITargetError(null);
    setAIClarificationQuestion(null);
    try {
      const segmentSelection = overrides.segments || aiSegmentTarget;
      const buyerLevelSelection = overrides.buyerLevels || aiBuyerLevelTarget;
      const selectedSegments = segmentSelection.includes('full_list') ? REAL_SEGMENT_VALUES : segmentSelection;
      const result = await post('/api/ai-campaigns', {
        prompt: finalPrompt,
        image_urls: aiImageUrls.length ? aiImageUrls : undefined,
        segment_target: selectedSegments,
        buyer_level_target: buyerLevelSelection,
        email_count: overrides.emailCount,
      });
      if (result.needs_clarification) {
        setAIClarificationQuestion(result.question);
        setAIGenerating(false);
        return;
      }
      setAICampaignId(result.campaign.id);
      setAIEmails(result.emails);
      setAICampaignSummary(result.campaign_summary || '');
      setAIDefaults(result.defaults_announced);
      setAIMessages([
        { role: 'assistant', content: result.campaign_summary || `Generated ${result.emails.length}-email campaign.` },
      ]);
      setAIAgentState('review');
    } catch (err) {
      const message = String(err.message || 'Generation failed');
      const isProviderOverloaded = /overloaded|temporarily unavailable|temporarily overloaded|529/i.test(message);
      setAIError(isProviderOverloaded
        ? 'Generation failed: AI provider is temporarily overloaded. Wait a minute and try again.'
        : `Generation failed: ${message}. Try a simpler prompt or fewer emails.`
      );
    } finally {
      setAIGenerating(false);
    }
  };

  const confirmAIGeneration = () => {
    const confirmation = aiGenerationConfirm;
    const finalPrompt = buildConfirmedPrompt(confirmation);
    const segmentSelection = confirmation?.selectedSegments || aiSegmentTarget;
    const buyerLevelSelection = confirmation?.selectedBuyerLevels || aiBuyerLevelTarget;
    setAISegmentTarget(segmentSelection);
    setAIBuyerLevelTarget(buyerLevelSelection);
    setAIGenerationConfirm(null);
    executeAIGeneration(finalPrompt, {
      segments: segmentSelection,
      buyerLevels: buyerLevelSelection,
      emailCount: confirmation?.emailCount,
    });
  };

  const handleAIClarificationContinue = () => {
    const combined = `${aiPrompt} — ${aiClarificationAnswer}`;
    setAIPrompt(combined);
    setAIClarificationAnswer('');
    handleAIGenerate(combined);
  };

  // ── Voice recording ──────────────────────────────────────────────────────
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/ogg';
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      const chunks = [];
      mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunks, { type: mimeType });
        const fd = new FormData();
        fd.append('audio', blob, 'voice.webm');
        setAIError(null);
        try {
          const res = await fetch('/api/transcribe-voice', { method: 'POST', body: fd });
          const data = await res.json();
          if (data.transcript) {
            setAIPrompt(prev => prev ? `${prev} ${data.transcript}` : data.transcript);
          } else {
            setAIError('Transcription failed. Try again.');
          }
        } catch (err) {
          setAIError('Transcription failed: ' + err.message);
        }
        setAIRecording(false);
        setAIMediaRecorder(null);
      };
      mediaRecorder.start();
      setAIMediaRecorder(mediaRecorder);
      setAIRecording(true);
    } catch (err) {
      setAIError('Microphone access denied. Please allow microphone access in your browser.');
    }
  };

  const stopRecording = () => {
    if (aiMediaRecorder) aiMediaRecorder.stop();
  };

  const updateEmail = (emailNumber, updates) => {
    setAIEmails(prev => prev.map(e =>
      e.email_number === emailNumber ? { ...e, ...updates } : e
    ));
  };

  const updateTierBody = (emailNumber, tier, newBody) => {
    setAIEmails(prev => prev.map(e =>
      e.email_number === emailNumber
        ? { ...e, tiers: { ...e.tiers, [tier]: { ...(e.tiers?.[tier] || {}), body: newBody } } }
        : e
    ));
  };

  const updateTierTrigger = (emailNumber, tier, updates) => {
    setAIEmails(prev => prev.map(e =>
      e.email_number === emailNumber
        ? {
            ...e,
            tiers: {
              ...e.tiers,
              [tier]: {
                ...(e.tiers?.[tier] || {}),
                trigger_email: { ...(e.tiers?.[tier]?.trigger_email || {}), ...updates },
              },
            },
          }
        : e
    ));
  };

  const updateTriggerEmail = (emailNumber, updates) => {
    setAIEmails(prev => prev.map(e =>
      e.email_number === emailNumber
        ? { ...e, trigger_email: { ...(e.trigger_email || {}), ...updates } }
        : e
    ));
  };

  const handleAIFeedback = async () => {
    if (!aiFeedback.trim() || !aiCampaignId) return;
    setAIRevising(true);
    const userMsg = aiFeedback;
    setAIMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setAIFeedback('');
    try {
      const result = await post(`/api/ai-campaigns/${aiCampaignId}/feedback`, {
        feedback: userMsg,
        current_emails: aiEmails,
      });
      // Merge revised emails into current state
      if (result.revised_emails && result.revised_emails.length > 0) {
        setAIEmails(prev => {
          const updated = [...prev];
          for (const revised of result.revised_emails) {
            const idx = updated.findIndex(e => e.email_number === revised.email_number);
            if (idx !== -1) updated[idx] = { ...updated[idx], ...revised };
          }
          return updated;
        });
      }
      setAIMessages(prev => [...prev, { role: 'assistant', content: result.revision_summary || 'Revisions applied.' }]);
    } catch (err) {
      setAIMessages(prev => [...prev, { role: 'assistant', content: `Error: ${err.message}` }]);
    } finally {
      setAIRevising(false);
    }
  };

  const runAIQuickEdit = async (email, target, instruction) => {
    if (!aiCampaignId || !email) return;
    setAIRevising(true);
    const userMsg = `For email ${email.email_number || 1}, revise the ${target}: ${instruction}. Keep the same main+trigger pair structure.`;
    setAIMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    try {
      const result = await post(`/api/ai-campaigns/${aiCampaignId}/feedback`, {
        feedback: userMsg,
        current_emails: [email],
      });
      if (result.revised_emails && result.revised_emails.length > 0) {
        setAIEmails(prev => prev.map(existing => {
          const revised = result.revised_emails.find(item => item.email_number === existing.email_number);
          return revised ? { ...existing, ...revised } : existing;
        }));
      }
      setAIMessages(prev => [...prev, { role: 'assistant', content: result.revision_summary || 'Quick edit applied.' }]);
    } catch (err) {
      setAIMessages(prev => [...prev, { role: 'assistant', content: `Error: ${err.message}` }]);
    } finally {
      setAIRevising(false);
    }
  };

  const handleAIApprove = async () => {
    if (!aiCampaignId || aiEmails.length === 0) return;
    setAIApproving(true);
    try {
      const result = await post(`/api/ai-campaigns/${aiCampaignId}/approve`, { emails: aiEmails });
      setAIApprovedResult(result);
      setAIAgentState('approved');
      loadDrafts();
    } catch (err) {
      setMessage(`Approval failed: ${err.message}`);
      setTimeout(() => setMessage(''), 5000);
    } finally {
      setAIApproving(false);
    }
  };

  const resetAIAgent = () => {
    clearJsonCache(COMPOSE_AI_CACHE_KEY);
    setAIAgentState('prompt');
    setAIPrompt('');
    setAIImageUrls([]);
    setAIImageUploading(false);
    setAICampaignId(null);
    setAIEmails([]);
    setAICampaignSummary('');
    setAIDefaults(null);
    setAIClarificationQuestion(null);
    setAIClarificationAnswer('');
    setAIFeedback('');
    setAIMessages([]);
    setAIApprovedResult(null);
    setAIMediaNotice(null);
    setAIGenerationConfirm(null);
    setAITargetError(null);
    setAISegmentTarget([]);
    setAIBuyerLevelTarget([]);
    setEmailExpandedTrigger({});
    setEmailBodyEditMode({});
    setTriggerBodyEditMode({});
    setActiveTierTab({});
  };

  const inputStyle = {
    width: '100%', padding: '10px 12px', background: 'var(--bg)', border: '1px solid var(--border)',
    borderRadius: 8, color: '#FAFAFA', fontSize: 13, fontFamily: "'DM Sans', sans-serif",
    boxSizing: 'border-box',
  };

  const labelStyle = { fontSize: 11, color: 'var(--muted)', fontWeight: 600, marginBottom: 4, display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' };

  return (
    <div>
      {/* ── AI Writing Agent — approved dark writer mockup ─────────────────── */}
      <div className="ecc-writer-shell">
        <div className="ecc-writer-panel">
          <div className="ecc-writer-head">
            <span className="ecc-writer-title">✍️ AI writing agent</span>
            <span className="ecc-writer-model">✨ Claude Opus 4.8</span>
          </div>

          <div className="ecc-writer-body">
            <p className="ecc-writer-sub">Describe one email in plain language. The agent drafts the email and its click-triggered follow-up as a pair.</p>
            <textarea
              className="ecc-writer-textarea"
              value={aiPrompt}
              onChange={e => setAIPrompt(e.target.value)}
              onPaste={handleAIPromptPaste}
              onDrop={handleAIPromptDrop}
              onDragOver={e => e.preventDefault()}
              placeholder="Pitch low-tier scanner owners on upgrading to the Freedom Income Engine using Alan's 7-month story. Friendly, direct, one clear CTA."
              onKeyDown={e => { if (e.key === 'Enter' && e.metaKey) handleAIGenerate(); }}
            />
            <p className="ecc-writer-hint">Link shortcuts: scanner, FIE, roadmap, free course, execution alerts</p>

            <div className="ecc-writer-selectors">
              <div>
                <div className="ecc-writer-label">Lists to hit</div>
                <div className="ecc-writer-chips">
                  {AI_SEGMENT_OPTIONS.filter(option => option.value !== 'full_list').map(({ value, label }) => {
                    const active = aiSegmentTarget.includes(value) || aiSegmentTarget.includes('full_list');
                    return (
                      <button
                        type="button"
                        key={value}
                        className={`ecc-writer-chip ${active ? 'on' : ''}`}
                        onClick={() => {
                          const current = aiSegmentTarget.filter(v => v !== 'full_list' && v !== value);
                          const next = active && !aiSegmentTarget.includes('full_list') ? current : [...current, value];
                          setAISegmentTarget(next);
                          if (next.length > 0) setAITargetError(null);
                        }}
                      >{label}</button>
                    );
                  })}
                </div>
              </div>
              <div>
                <div className="ecc-writer-label">Trigger audience</div>
                <div className="ecc-writer-chips">
                  {TIERS_ORDER.map(tier => {
                    const active = aiBuyerLevelTarget.includes(tier);
                    return (
                      <button
                        type="button"
                        key={tier}
                        className={`ecc-writer-chip ${active ? 'on' : ''}`}
                        onClick={() => {
                          setAIBuyerLevelTarget(active ? [] : [tier]);
                        }}
                      >{TIER_LABELS_AI[tier]}</button>
                    );
                  })}
                </div>
              </div>
            </div>

            {aiTargetError && <div className="ecc-writer-error">{aiTargetError}</div>}
            {aiError && <div className="ecc-writer-error">{aiError}</div>}
            {aiMediaNotice && !aiError && <div className="ecc-writer-success">{aiMediaNotice}</div>}

            {aiImageUrls.length > 0 && (
              <div className="image-attachment-strip" aria-label="Attached prompt images">
                {aiImageUrls.map((url, idx) => (
                  <div className="image-attachment-chip" key={`${url}-${idx}`}>
                    <img src={url} alt={`Attached ${idx + 1}`} />
                    <span>{idx + 1}</span>
                    <button type="button" aria-label={`Remove image ${idx + 1}`} onClick={() => removeAIImage(idx)}>×</button>
                  </div>
                ))}
                <div className="image-attachment-count">{aiImageUrls.length}/{MAX_IMAGE_ATTACHMENTS} images</div>
              </div>
            )}

            <div className="ecc-writer-actions">
              <button
                className="ecc-writer-btn ecc-writer-primary"
                onClick={() => handleAIGenerate()}
                disabled={aiGenerating || !aiPrompt.trim() || aiSegmentTarget.length === 0}
              >
                {aiGenerating ? <><IconSpinner /> Generating email...</> : <>⚡ Generate email</>}
              </button>
              <label className="ecc-writer-btn ecc-writer-secondary">
                {aiImageUploading ? <IconSpinner /> : <IconImage />} {aiImageUploading ? 'Uploading...' : 'Add images'}
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ display: 'none' }}
                  disabled={aiGenerating}
                  onChange={async (e) => {
                    await uploadAIImageFiles(e.target.files, 'upload');
                    e.target.value = '';
                  }}
                />
              </label>
              <button className="ecc-writer-btn ecc-writer-secondary" onClick={aiRecording ? stopRecording : startRecording} disabled={aiGenerating}>
                {aiRecording ? <><span className="recording-pulse" aria-hidden="true" /><IconStop /> Stop recording</> : <><IconMic /> Voice prompt</>}
              </button>
            </div>

            {aiClarificationQuestion && (
              <div className="ecc-writer-clarify">
                <strong>{aiClarificationQuestion}</strong>
                <div className="ecc-writer-revise-row">
                  <input value={aiClarificationAnswer} onChange={e => setAIClarificationAnswer(e.target.value)} placeholder="Answer..." />
                  <button onClick={handleAIClarificationContinue} disabled={!aiClarificationAnswer.trim()}>Continue</button>
                </div>
              </div>
            )}
          </div>
        </div>

        {aiAgentState === 'review' && aiEmails.length > 0 && (
          <div className="ecc-writer-generated">
            <div className="ecc-writer-section-h">👁 Generated — review the pair</div>
            {aiCampaignSummary && <p className="ecc-writer-review-summary">{aiCampaignSummary}</p>}
            {aiEmails.map(email => {
              const tierKey = email.buyer_level_target?.[0];
              const legacyTier = email.tiers ? (TIERS_ORDER.find(tier => email.tiers?.[tier]) || 'non_buyer') : null;
              const mainBody = email.body || email.tiers?.[legacyTier]?.body || '';
              const triggerEmail = email.trigger_email || email.tiers?.[legacyTier]?.trigger_email || null;
              const metaSegments = segmentLabel(email.segment_target || aiDefaults?.segment_target || aiSegmentTarget);
              const metaTier = buyerLevelLabel(email.buyer_level_target || (legacyTier ? [legacyTier] : aiDefaults?.buyer_level_target));
              return (
                <React.Fragment key={email.pair_key || email.email_number}>
                  <div className="ecc-writer-card">
                    <div className="ecc-writer-tags">
                      <span className="ecc-writer-tag main">Main email</span>
                      <span className="ecc-writer-tag meta">{metaSegments}</span>
                      <span className="ecc-writer-tag meta">{metaTier}</span>
                      <span className="ecc-writer-tag meta">broadcast</span>
                    </div>
                    <p className="ecc-writer-subject">{email.subject}</p>
                    <p className="ecc-writer-preview">{stripHtmlToText(mainBody).slice(0, 240)}{stripHtmlToText(mainBody).length > 240 ? '...' : ''}</p>
                    <div className="ecc-writer-qchips">
                      <button onClick={() => runAIQuickEdit(email, 'main email', 'tighten the copy without changing the offer or CTA')} disabled={aiRevising}>↘ Tighten</button>
                      <button onClick={() => runAIQuickEdit(email, 'main email', 'make the hook and CTA punchier')} disabled={aiRevising}>🔥 Punchier</button>
                      <button onClick={() => runAIQuickEdit(email, 'main email', 'regenerate the main email from scratch while preserving the same brief')} disabled={aiRevising}>↻ Regenerate</button>
                    </div>
                  </div>
                  {triggerEmail && (
                    <div className="ecc-writer-card trigger">
                      <div className="ecc-writer-tags">
                        <span className="ecc-writer-tag trig">⚡ Trigger — fires on click</span>
                      </div>
                      <p className="ecc-writer-subject">{triggerEmail.subject}</p>
                      <p className="ecc-writer-preview">{stripHtmlToText(triggerEmail.body).slice(0, 220)}{stripHtmlToText(triggerEmail.body).length > 220 ? '...' : ''}</p>
                      <div className="ecc-writer-qchips">
                        <button onClick={() => runAIQuickEdit(email, 'trigger email', 'tighten the follow-up without changing the CTA')} disabled={aiRevising}>↘ Tighten</button>
                        <button onClick={() => runAIQuickEdit(email, 'trigger email', 'make the clicked-follow-up angle punchier')} disabled={aiRevising}>🔥 Punchier</button>
                        <button onClick={() => runAIQuickEdit(email, 'trigger email', 'regenerate the trigger email from scratch based on the main email')} disabled={aiRevising}>↻ Regenerate</button>
                      </div>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
            <div className="ecc-writer-revise-row">
              <input
                value={aiFeedback}
                onChange={e => setAIFeedback(e.target.value)}
                placeholder='Tell the agent how to revise — e.g. "shorter subject" or "lead with the result"'
                onKeyDown={e => { if (e.key === 'Enter') handleAIFeedback(); }}
              />
            </div>
            <div className="ecc-writer-approve-row">
              <button className="ecc-writer-btn ecc-writer-green" onClick={handleAIApprove} disabled={aiApproving || aiRevising}>{aiApproving ? 'Creating drafts...' : '✓ Approve & create drafts'}</button>
              <button className="ecc-writer-btn ecc-writer-secondary" onClick={resetAIAgent}>Discard</button>
              <span>Nothing sends until you approve each one.</span>
            </div>
          </div>
        )}

        {aiAgentState === 'approved' && aiApprovedResult && (
          <div className="ecc-writer-generated">
            <div className="ecc-writer-card">
              <p className="ecc-writer-subject">Drafts created</p>
              <p className="ecc-writer-preview">{aiApprovedResult.message}</p>
              <div className="ecc-writer-qchips"><button onClick={resetAIAgent}>Start new email</button></div>
            </div>
          </div>
        )}

        <div className="ecc-writer-metrics">
          <div className="ecc-writer-metric"><div>Needs review</div><strong>{reviewDrafts.length}</strong></div>
          <div className="ecc-writer-metric"><div>Scheduled</div><strong>{composeStatus.scheduled.length}</strong></div>
          <div className="ecc-writer-metric"><div>Active triggers</div><strong>{composeStatus.triggers.length}</strong></div>
          <div className="ecc-writer-metric"><div>Sent 7 days</div><strong>{composeStatus.recentSent.length}</strong></div>
        </div>
      </div>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700 }}>Compose</h2>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {composeTabs.map(tab => (
            <button key={tab.key} onClick={() => setComposeTab(tab.key)} className={`compose-tab-button ${composeTab === tab.key ? 'active' : ''}`}>
              <span>{tab.label}</span>
              <strong>{tab.count}</strong>
            </button>
          ))}
          <button onClick={() => { setShowGenerate(!showGenerate); setShowCreate(false); }} style={{
            padding: '6px 16px', fontSize: 12, border: 'none', borderRadius: 6, cursor: 'pointer',
            background: showGenerate ? '#7C3AED' : '#5B21B6', color: '#fff', fontWeight: 700,
          }}>⚡ AI Generate</button>
          <button onClick={() => { setShowCreate(!showCreate); setShowGenerate(false); }} style={{
            padding: '6px 16px', fontSize: 12, border: 'none', borderRadius: 6, cursor: 'pointer',
            background: 'var(--green)', color: '#fff', fontWeight: 700,
          }}>+ New Draft</button>
        </div>
      </div>

      <div className="compose-status-strip">
        <StatusCard
          label="Needs Review"
          value={reviewDrafts.length}
          subline={reviewDrafts.length ? 'Drafts awaiting approval' : 'All caught up'}
          tone={reviewDrafts.length ? 'warning' : ''}
        />
        <StatusCard
          label="Scheduled"
          value={composeStatus.scheduled.length}
          subline={composeStatus.scheduled.length ? `Next: ${formatScheduledAt(composeStatus.scheduled[0]?.scheduled_at)}` : 'Nothing scheduled'}
        />
        <StatusCard
          label="Queued"
          value={composeStatus.queued.length}
          subline={composeStatus.queued.length ? 'Due now / next cron' : 'No due broadcasts'}
        />
        <StatusCard
          label="Active Triggers"
          value={composeStatus.triggers.length}
          subline={composeStatus.triggers.length ? 'Firing on clicks' : 'No approved triggers'}
        />
        <StatusCard
          label="Sent 7 Days"
          value={composeStatus.recentSent.length}
          subline={`${libraryDrafts.length} total library items`}
        />
      </div>

      {/* ── Message banner ─────────────────────────────────────────────────── */}
      {message && (
        <div style={{ padding: '10px 16px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, marginBottom: 16, fontSize: 13 }}>
          {message}
        </div>
      )}

      {/* ── AI Generate Panel ──────────────────────────────────────────────── */}
      {showGenerate && (
        <div style={{ background: 'var(--surface)', border: '1px solid #5B21B6', borderRadius: 14, padding: 24, marginBottom: 20 }}>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>⚡ AI Draft Generator</div>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 20 }}>
            Generates a main content email + linked trigger sales email — both saved as drafts for your review.
          </div>

          {/* Row 1: Segment + Email Type */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={labelStyle}>Segment</label>
              <select value={gen.segment} onChange={e => setGen({ ...gen, segment: e.target.value })} style={inputStyle}>
                <option value="full_list">Full List</option>
                <option value="hot">Hot (3d clickers)</option>
                <option value="warm">Warm (7d clickers)</option>
                <option value="hot,warm">Hot + Warm</option>
                <option value="cool">Cool (14d opens)</option>
                <option value="cold">Cold (30d+)</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Email Type</label>
              <select value={gen.emailType} onChange={e => setGen({ ...gen, emailType: e.target.value })} style={inputStyle}>
                {['story', 'training', 'proof', 'announcement', 'newsletter'].map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Row 2: Topics */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            <div>
              <label style={labelStyle}>Main Email Topic</label>
              <input
                placeholder="e.g. Why I trade cash-secured puts every Monday"
                value={gen.contentTopic}
                onChange={e => setGen({ ...gen, contentTopic: e.target.value })}
                style={inputStyle}
              />
              <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4 }}>Content angle for the broadcast email</div>
            </div>
            <div>
              <label style={labelStyle}>Trigger Email Topic</label>
              <input
                placeholder="e.g. How this session will map out your first trade"
                value={gen.triggerTopic}
                onChange={e => setGen({ ...gen, triggerTopic: e.target.value })}
                style={inputStyle}
              />
              <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4 }}>Framing angle for the follow-up sales email (optional)</div>
            </div>
          </div>

          {/* Row 2: Offer details */}
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Weekly Offer (for trigger email)
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={labelStyle}>Offer Name</label>
              <input value={gen.offerName} onChange={e => setGen({ ...gen, offerName: e.target.value })} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Price ($)</label>
              <input type="number" value={gen.offerPrice} onChange={e => setGen({ ...gen, offerPrice: e.target.value })} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Normal Price ($)</label>
              <input type="number" value={gen.offerNormalPrice} onChange={e => setGen({ ...gen, offerNormalPrice: e.target.value })} style={inputStyle} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={labelStyle}>Scarcity</label>
              <input placeholder="e.g. 25 spots only" value={gen.offerScarcity} onChange={e => setGen({ ...gen, offerScarcity: e.target.value })} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Deadline</label>
              <input placeholder="e.g. Friday at midnight" value={gen.offerDeadline} onChange={e => setGen({ ...gen, offerDeadline: e.target.value })} style={inputStyle} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: 12, marginBottom: 20 }}>
            <div>
              <label style={labelStyle}>Main Email CTA URL</label>
              <input
                placeholder="e.g. https://freedomincomeoptions.com/roadmap/"
                value={gen.offerMainUrl}
                onChange={e => setGen({ ...gen, offerMainUrl: e.target.value })}
                style={inputStyle}
              />
              <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4 }}>Link in the content email</div>
            </div>
            <div>
              <label style={labelStyle}>Trigger Email Buy URL</label>
              <input
                placeholder="e.g. https://members.freedomincomeoptions.com/register/"
                value={gen.offerTriggerUrl}
                onChange={e => setGen({ ...gen, offerTriggerUrl: e.target.value })}
                style={inputStyle}
              />
              <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4 }}>Checkout link in the trigger email</div>
            </div>
            <div>
              <label style={labelStyle}>Angle / Hook</label>
              <input placeholder="What's the core value prop?" value={gen.offerAngle} onChange={e => setGen({ ...gen, offerAngle: e.target.value })} style={inputStyle} />
            </div>
          </div>

          {/* Generate button */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              onClick={handleGenerate}
              disabled={generating}
              style={{
                padding: '10px 24px', fontSize: 13, border: 'none', borderRadius: 8, cursor: generating ? 'not-allowed' : 'pointer',
                background: generating ? '#374151' : '#5B21B6', color: generating ? 'var(--muted)' : '#fff',
                fontWeight: 700, transition: 'background 0.2s',
              }}
            >
              {generating ? '⏳ Generating...' : '⚡ Generate Draft Pair'}
            </button>
            <span style={{ fontSize: 11, color: 'var(--muted)' }}>~15-20 seconds · main + trigger · both saved as drafts</span>
          </div>

          {/* Result preview */}
          {generateResult && (
            <div style={{ marginTop: 20, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {[{ label: '📨 Main Email', draft: generateResult.main }, { label: '⚡ Trigger Email', draft: generateResult.trigger }].map(({ label, draft }) => (
                <div key={draft.id} style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10, padding: 14 }}>
                  <div style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 700, marginBottom: 6, textTransform: 'uppercase' }}>{label}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>{draft.subject}</div>
                  <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: "'JetBrains Mono', monospace", marginBottom: 8 }}>
                    {draft.preview_text}
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <button onClick={() => handleAction(draft.id, 'send-test')} style={{ ...btnStyle('#8B5CF6'), opacity: 0.85 }}>Test</button>
                    <span style={{ color: 'var(--border)', fontSize: 16, userSelect: 'none' }}>|</span>
                    <button onClick={() => handleAction(draft.id, 'approve')} style={btnStyle('#10B981')}>Approve</button>
                    <button onClick={() => handleAction(draft.id, 'reject')} style={btnStyle('#EF4444')}>Reject</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Manual Create Form ─────────────────────────────────────────────── */}
      {showCreate && (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 20, marginBottom: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 16 }}>Create New Draft</div>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 12 }}>
            Paste plain text or HTML. Plain text will be auto-converted to HTML (paragraphs, clickable links).
          </div>
          <div style={{ display: 'grid', gap: 12 }}>
            <input placeholder="Subject line (max 50 chars)" value={newDraft.subject} maxLength={50}
              onChange={e => setNewDraft({ ...newDraft, subject: e.target.value })} style={inputStyle} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12 }}>
              <div style={{ ...inputStyle, padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 6, height: 'auto' }}>
                <div style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 600, marginBottom: 2 }}>SEGMENTS (check all that apply)</div>
                {SEGMENT_OPTIONS.map(({ value, label }) => {
                  const isFull = value === 'full_list';
                  const currentSegs = newDraft.segment_target || [];
                  const isChecked = currentSegs.includes(value);
                  return (
                    <label key={value} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer', opacity: isSegmentOptionDisabled(currentSegs, value) ? 0.4 : 1 }}>
                      <input type="checkbox"
                        checked={isChecked}
                        disabled={isSegmentOptionDisabled(currentSegs, value)}
                        onChange={e => {
                          const updated = isFull
                            ? (e.target.checked ? ['full_list'] : [])
                            : toggleSegmentSelection(currentSegs, value, e.target.checked);
                          setNewDraft({ ...newDraft, segment_target: updated.length ? updated : ['full_list'] });
                        }} />
                      <span>{label}</span>
                    </label>
                  );
                })}
              </div>
              <div style={{ ...inputStyle, padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 6, height: 'auto' }}>
                <div style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 600, marginBottom: 2 }}>AUDIENCE (leave all unchecked = all)</div>
                {[['non_buyer','Non-Buyers'],['low_tier','Low-Tier'],['core','Core Members']].map(([val, label]) => (
                  <label key={val} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer', color: TIER_BG[val] }}>
                    <input type="checkbox" checked={(newDraft.buyer_level_target || []).includes(val)}
                      onChange={e => {
                        const current = newDraft.buyer_level_target || [];
                        const updated = e.target.checked ? [...current, val] : current.filter(v => v !== val);
                        setNewDraft({ ...newDraft, buyer_level_target: updated });
                      }} />
                    <strong>{label}</strong>
                  </label>
                ))}
              </div>
              <select value={newDraft.email_type} onChange={e => setNewDraft({ ...newDraft, email_type: e.target.value })} style={inputStyle}>
                {['story', 'training', 'promotion', 'urgency', 'proof', 'announcement', 'triggered_offer'].map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
              <select value={newDraft.send_mode} onChange={e => setNewDraft({ ...newDraft, send_mode: e.target.value })} style={inputStyle}>
                <option value="broadcast">Broadcast</option>
                <option value="triggered">Triggered</option>
              </select>
            </div>

            {/* Source broadcast — only for triggered drafts */}
            {newDraft.send_mode === 'triggered' && (
              <div>
                <label style={labelStyle}>Source Broadcast (this trigger fires when that broadcast is clicked)</label>
                <select
                  value={newDraft.source_broadcast_id || ''}
                  onChange={e => setNewDraft({ ...newDraft, source_broadcast_id: e.target.value })}
                  style={inputStyle}
                >
                  <option value="">— Select source broadcast —</option>
                  {drafts.filter(dr => dr.send_mode === 'broadcast' && dr.status !== 'rejected').map(br => (
                    <option key={br.id} value={br.id}>[{br.status}] {br.subject}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Trigger link — only for broadcasts */}
            {newDraft.send_mode === 'broadcast' && (
              <div>
                <label style={labelStyle}>Linked Trigger Email</label>
                <select
                  value={newDraft.trigger_draft_id}
                  onChange={e => setNewDraft({ ...newDraft, trigger_draft_id: e.target.value })}
                  style={{ ...inputStyle, borderColor: newDraft.trigger_draft_id ? '#10B981' : '#F59E0B' }}
                >
                  <option value="">⚠️ No trigger linked — clicks won't fire a follow-up</option>
                  {triggerDrafts.map(t => (
                    <option key={t.id} value={t.id}>✓ {t.subject} [{t.status}]</option>
                  ))}
                </select>
                {!newDraft.trigger_draft_id && (
                  <div style={{ fontSize: 11, color: '#F59E0B', marginTop: 4 }}>
                    ⚠️ Broadcast will send without a trigger. Clicks won't fire a follow-up sales email.
                  </div>
                )}
                {newDraft.trigger_draft_id && (
                  <div style={{ fontSize: 11, color: '#10B981', marginTop: 4 }}>
                    ✓ Trigger linked — clicks will fire the follow-up automatically.
                  </div>
                )}
              </div>
            )}

            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <label style={labelStyle}>Body {showPreview ? '(Preview)' : '(Edit)'}</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {/* Image upload — appends to body */}
                  <label style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 26, height: 26, borderRadius: 6, background: 'var(--surface)',
                    border: '1px solid var(--border)', cursor: 'pointer', fontSize: 16, color: 'var(--muted)',
                  }} title="Upload image">
                    🖼
                    <input type="file" accept="image/*" style={{ display: 'none' }}
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        const fd = new FormData();
                        fd.append('image', file);
                        try {
                          const res = await fetch('/api/upload-image', { method: 'POST', body: fd });
                          const data = await res.json();
                          if (data.url) {
                            const imgTag = `<img src="${data.url}" alt="" style="max-width:400px;width:100%;height:auto;display:block;margin:12px 0;" />`;
                            setNewDraft(prev => ({ ...prev, body: (prev.body || '') + imgTag }));
                          }
                        } catch (err) { console.error('Image upload failed', err); }
                        e.target.value = '';
                      }}
                    />
                  </label>
                </div>
              </div>
              {showPreview ? (
                <div
                  style={{
                    background: '#fff', color: '#1a1a1a', padding: 16, borderRadius: 8,
                    border: '1px solid var(--border)', fontSize: 14, lineHeight: 1.6,
                    minHeight: 200, maxHeight: 420, overflow: 'auto',
                  }}
                  dangerouslySetInnerHTML={{ __html: ensureHtmlBody(newDraft.body) }}
                />
              ) : (
                <textarea
                  placeholder="Email body — paste plain text or HTML"
                  value={newDraft.body}
                  rows={12}
                  onChange={e => setNewDraft({ ...newDraft, body: e.target.value })}
                  style={{ ...inputStyle, fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}
                />
              )}
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleCreate} style={{ padding: '10px 20px', fontSize: 13, border: 'none', borderRadius: 8, cursor: 'pointer', background: 'var(--blue)', color: '#fff', fontWeight: 700 }}>
                Save Draft
              </button>
              <button onClick={() => setShowPreview(!showPreview)} style={{ padding: '10px 20px', fontSize: 13, border: 'none', borderRadius: 8, cursor: 'pointer', background: showPreview ? '#7C3AED' : '#7C3AED33', color: showPreview ? '#fff' : '#7C3AED', fontWeight: 600 }}>
                {showPreview ? '✏️ Edit' : '👁 Preview'}
              </button>
              <button onClick={() => { clearJsonCache(COMPOSE_MANUAL_CACHE_KEY); setNewDraft(DEFAULT_NEW_DRAFT); setShowCreate(false); setShowPreview(false); }} style={{ padding: '10px 20px', fontSize: 13, border: 'none', borderRadius: 8, cursor: 'pointer', background: 'var(--border)', color: 'var(--muted)', fontWeight: 600 }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Drafts list ────────────────────────────────────────────────────── */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--muted)' }}>Loading...</div>
      ) : drafts.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--muted)', fontSize: 14 }}>
          No drafts found. Use ⚡ AI Generate or + New Draft to create your first email.
        </div>
      ) : visibleDrafts.length === 0 ? (
        <EmptyComposeTab
          tab={composeTab}
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {visibleDrafts.map(d => {
            const sendBlockReason = getDraftSendBlockReason(d);
            const sendBlockedStyle = sendBlockReason ? { opacity: 0.45, cursor: 'not-allowed' } : null;
            return (
            <div key={d.id} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: '16px 20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
                    {(() => {
                      const status = effectiveStatus(d);
                      return (
                    <span style={{
                      fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 4,
                      background: `${STATUS_COLORS[status] || STATUS_COLORS[d.status]}22`, color: STATUS_COLORS[status] || STATUS_COLORS[d.status],
                      textTransform: 'uppercase', fontFamily: "'JetBrains Mono', monospace",
                    }}>{status}</span>
                      );
                    })()}
                    <span style={{ fontSize: 10, color: 'var(--muted)', fontFamily: "'JetBrains Mono', monospace" }}>
                      {getDirectRecipients(d).length > 0 ? `Direct list (${getDirectRecipients(d).length})` : segmentLabel(d.segment_target)} · {d.email_type} · {d.send_mode}
                    </span>
                    {(() => {
                      const blt = Array.isArray(d.buyer_level_target) ? d.buyer_level_target : (d.buyer_level_target ? [d.buyer_level_target] : []);
                      if (blt.length === 0) return null;
                      // Use first tier's color if single, grey if mixed
                      const color = blt.length === 1 ? (TIER_BG[blt[0]] || '#71717A') : '#8B5CF6';
                      const label = blt.map(t => TIER_LABELS_AI[t] || t).join(' + ');
                      return (
                        <span style={{
                          fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
                          background: `${color}22`, color,
                          fontFamily: "'JetBrains Mono', monospace",
                          textTransform: 'uppercase',
                        }}>
                          {label}
                        </span>
                      );
                    })()}
                    {d.ai_provider && d.ai_provider !== 'manual' && (
                      <span style={{ fontSize: 10, color: '#7C3AED', fontFamily: "'JetBrains Mono', monospace", fontWeight: 700 }}>
                        ⚡ {d.ai_provider}
                      </span>
                    )}
                    {d.send_mode === 'broadcast' && hasLinkedTrigger(d) && (
                      <span style={{ fontSize: 10, color: '#10B981', fontFamily: "'JetBrains Mono', monospace", fontWeight: 700 }}>
                        {linkedTriggerLabel(d)}
                      </span>
                    )}
                    {d.send_mode === 'broadcast' && !hasLinkedTrigger(d) && d.status !== 'sent' && (
                      <span style={{ fontSize: 10, color: '#F59E0B', fontFamily: "'JetBrains Mono', monospace", fontWeight: 700 }}>
                        ⚠️ no trigger set
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4, color: String(d.subject || '').trim() ? 'var(--text)' : '#F59E0B' }}>
                    {String(d.subject || '').trim() || 'No subject — edit before sending'}
                  </div>
                  {d.preview_text && (
                    <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 4, fontStyle: 'italic' }}>{d.preview_text}</div>
                  )}
                  {(() => {
                    const schedule = getScheduleDisplay(d);
                    const timingLabel = getDraftTimingLabel(d);
                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
                        <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: "'JetBrains Mono', monospace" }}>
                          Created {formatCardDateTime(d.created_at)} · {
                            d.status === 'sent'
                              ? `${(d.recipients_count || 0).toLocaleString()} recipients`
                              : projectedCounts[d.id] != null
                                ? `~${projectedCounts[d.id].toLocaleString()} projected`
                                : '— projected'
                          }
                        </div>
                        {timingLabel && (
                          <div style={{
                            display: 'inline-flex', alignItems: 'center', width: 'fit-content',
                            padding: '4px 9px', borderRadius: 6,
                            background: '#0EA5E922', color: '#7DD3FC', fontSize: 11, fontWeight: 700,
                            fontFamily: "'JetBrains Mono', monospace",
                          }}>
                            {timingLabel}
                          </div>
                        )}
                        {schedule && (
                          <div style={{
                            display: 'inline-flex', alignItems: 'center', width: 'fit-content',
                            padding: '4px 9px', borderRadius: 6, background: schedule.background,
                            color: schedule.color, fontSize: 11, fontWeight: 700,
                            fontFamily: "'JetBrains Mono', monospace",
                          }}>
                            {schedule.label}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0, alignItems: 'center' }}>
                  {d.status === 'draft' && (
                    <>
                      <button onClick={() => handleEdit(d)} style={btnStyle('#3B82F6')}>Edit</button>
                      <button title={sendBlockReason || ''} onClick={() => handleAction(d.id, 'send-test')} style={{ ...btnStyle('#8B5CF6'), opacity: sendBlockReason ? 0.45 : 0.85, cursor: sendBlockReason ? 'not-allowed' : 'pointer' }}>Test</button>
                      <span style={{ color: 'var(--border)', fontSize: 16, userSelect: 'none' }}>|</span>
                      {d.send_mode !== 'triggered' && (
                        <button title={sendBlockReason || ''} onClick={() => openScheduleModal(d)} style={{ ...btnStyle('#8B5CF6'), ...(sendBlockedStyle || {}) }}>Schedule</button>
                      )}
                      <button title={sendBlockReason || ''} onClick={() => handleAction(d.id, 'approve')} style={{ ...btnStyle('#10B981'), ...(sendBlockedStyle || {}) }}>Approve</button>
                      <button onClick={() => handleAction(d.id, 'reject')} style={btnStyle('#EF4444')}>Reject</button>
                    </>
                  )}
                  {d.status === 'approved' && (
                    <>
                      <button title={sendBlockReason || ''} onClick={() => handleAction(d.id, 'send-test')} style={{ ...btnStyle('#8B5CF6'), opacity: sendBlockReason ? 0.45 : 0.85, cursor: sendBlockReason ? 'not-allowed' : 'pointer' }}>Test</button>
                      {d.send_mode !== 'triggered' && (
                        <>
                          <button title={sendBlockReason || ''} onClick={() => openScheduleModal(d)} style={{ ...btnStyle('#8B5CF6'), ...(sendBlockedStyle || {}) }}>Reschedule</button>
                          <button
                            title={sendBlockReason || 'Send this immediately instead of waiting for the 5-minute cron'}
                            onClick={() => openSendNowModal(d)}
                            style={{ ...btnStyle('#F59E0B'), ...(sendBlockedStyle || {}), fontWeight: 700 }}
                          >
                            ⚡ Send Now
                          </button>
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* ── Inline Edit Panel ──────────────────────────────────── */}
              {editingId === d.id && (
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
                  <div style={{ display: 'grid', gap: 10 }}>
                    <div>
                      <label style={labelStyle}>Subject</label>
                      <input value={editDraft.subject} maxLength={50}
                        onChange={e => setEditDraft({ ...editDraft, subject: e.target.value })}
                        style={inputStyle} />
                    </div>

                    {/* Send Mode / Segment / Buyer Level */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                      <div>
                        <label style={labelStyle}>Send Mode</label>
                        <select
                          value={editDraft.send_mode}
                          onChange={e => setEditDraft({ ...editDraft, send_mode: e.target.value })}
                          style={inputStyle}
                        >
                          <option value="broadcast">Broadcast</option>
                          <option value="triggered">Triggered</option>
                        </select>
                      </div>
                      <div>
                        <label style={labelStyle}>Segments</label>
                        <div style={{ ...inputStyle, padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 6, height: 'auto' }}>
                          <div style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 600, marginBottom: 2 }}>Check all that apply</div>
                          {SEGMENT_OPTIONS.map(({ value, label }) => {
                            const isFull = value === 'full_list';
                            const currentSegs = normalizeSegmentArr(editDraft.segment_target);
                            const isChecked = currentSegs.includes(value);
                            return (
                              <label key={value} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer', opacity: isSegmentOptionDisabled(currentSegs, value) ? 0.4 : 1 }}>
                                <input type="checkbox"
                                  checked={isChecked}
                                  disabled={isSegmentOptionDisabled(currentSegs, value)}
                                  onChange={e => {
                                    const updated = isFull
                                      ? (e.target.checked ? ['full_list'] : [])
                                      : toggleSegmentSelection(currentSegs, value, e.target.checked);
                                    setEditDraft({ ...editDraft, segment_target: updated.length ? updated : ['full_list'] });
                                  }} />
                                <span>{label}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                      <div>
                        <label style={labelStyle}>Audience Filter</label>
                        <div style={{ ...inputStyle, padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 6, height: 'auto' }}>
                          <div style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 600, marginBottom: 2 }}>Unchecked = all audiences</div>
                          {[['non_buyer','Non-Buyers'],['low_tier','Low-Tier'],['core','Core']].map(([val, label]) => {
                            const current = Array.isArray(editDraft.buyer_level_target) ? editDraft.buyer_level_target : (editDraft.buyer_level_target ? [editDraft.buyer_level_target] : []);
                            return (
                              <label key={val} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer', color: TIER_BG[val] }}>
                                <input type="checkbox" checked={current.includes(val)}
                                  onChange={e => {
                                    const updated = e.target.checked ? [...current, val] : current.filter(v => v !== val);
                                    setEditDraft({ ...editDraft, buyer_level_target: updated });
                                  }} />
                                <strong>{label}</strong>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    {/* Source broadcast — only for triggered drafts */}
                    {editDraft.send_mode === 'triggered' && (
                      <div>
                        <label style={labelStyle}>Source Broadcast (fires when this broadcast is clicked)</label>
                        <select
                          value={editDraft.source_broadcast_id || ''}
                          onChange={e => setEditDraft({ ...editDraft, source_broadcast_id: e.target.value })}
                          style={inputStyle}
                        >
                          <option value="">— Not linked to a broadcast —</option>
                          {drafts.filter(dr => dr.send_mode === 'broadcast' && dr.status !== 'rejected').map(br => (
                            <option key={br.id} value={br.id}>[{br.status}] {br.subject}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Trigger link — only for broadcasts */}
                    {editDraft.send_mode === 'broadcast' && (
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          <label style={{ ...labelStyle, marginBottom: 0 }}>Linked Trigger Email</label>
                          <button
                            type="button"
                            onClick={loadDrafts}
                            title="Refresh trigger list"
                            style={{
                              background: 'none', border: '1px solid var(--border)', borderRadius: 4,
                              cursor: 'pointer', fontSize: 12, color: 'var(--muted)', padding: '1px 6px',
                              lineHeight: 1.4,
                            }}
                          >↻</button>
                        </div>
                          <select
                            value={editDraft.trigger_draft_id}
                            onChange={e => setEditDraft({ ...editDraft, trigger_draft_id: e.target.value })}
                          style={{ ...inputStyle, borderColor: hasLinkedTrigger(editDraft) ? '#10B981' : '#F59E0B' }}
                        >
                          <option value="">⚠️ No trigger linked — clicks won't fire a follow-up</option>
                          {triggerDrafts.map(t => (
                            <option key={t.id} value={t.id}>✓ {t.subject} [{t.status}]</option>
                          ))}
                        </select>
                        {!hasLinkedTrigger(editDraft) && (
                          <div style={{ fontSize: 11, color: '#F59E0B', marginTop: 4 }}>
                            ⚠️ No trigger set. Clicks from this broadcast won't fire a follow-up sales email.
                          </div>
                        )}
                        {hasLinkedTrigger(editDraft) && (
                          <div style={{ fontSize: 11, color: '#10B981', marginTop: 4 }}>
                            ✓ {linkedTriggerLabel(editDraft).replace(/^⚡\s*/, '') || 'Trigger linked'} — clicks will fire the follow-up automatically.
                          </div>
                        )}
                      </div>
                    )}

                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                        <label style={labelStyle}>Body {editPreview ? '(Preview)' : '(Edit)'}</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          {/* Image upload for edit panel */}
                          <label style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            minWidth: 26, height: 26, borderRadius: 6, background: 'var(--surface)',
                            border: '1px solid var(--border)', cursor: editImageUploading ? 'wait' : 'pointer', fontSize: 14, color: 'var(--muted)', padding: '0 6px', gap: 4,
                          }} title="Upload or replace draft image">
                            🖼 {editImageUploading && <span style={{ fontSize: 10 }}>Uploading…</span>}
                            <input type="file" accept="image/*" style={{ display: 'none' }}
                              disabled={editImageUploading}
                              onChange={async (e) => {
                                const file = e.target.files[0];
                                if (file) await handleEditImageFiles([file]);
                                e.target.value = '';
                              }}
                            />
                          </label>
                        </div>
                      </div>
                      {editPreview ? (
                        <div
                          style={{
                            background: '#fff', color: '#1a1a1a', padding: 16, borderRadius: 8,
                            border: '1px solid var(--border)', fontSize: 14, lineHeight: 1.6,
                            minHeight: 200, maxHeight: 400, overflow: 'auto',
                          }}
                          dangerouslySetInnerHTML={{ __html: ensureHtmlBody(editDraft.body) }}
                        />
                      ) : (
                        <textarea value={editDraft.body} rows={12}
                          onPaste={handleEditBodyPaste}
                          onDrop={handleEditBodyDrop}
                          onDragOver={e => e.preventDefault()}
                          onChange={e => setEditDraft({ ...editDraft, body: e.target.value })}
                          style={{ ...inputStyle, fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }} />
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={handleSaveEdit} disabled={saving} style={{
                        padding: '8px 16px', fontSize: 12, border: 'none', borderRadius: 6, cursor: saving ? 'not-allowed' : 'pointer',
                        background: 'var(--blue)', color: '#fff', fontWeight: 700,
                      }}>
                        {saving ? 'Saving...' : 'Save Changes'}
                      </button>
                      <button onClick={() => setEditPreview(!editPreview)} style={{
                        padding: '8px 16px', fontSize: 12, border: 'none', borderRadius: 6, cursor: 'pointer',
                        background: editPreview ? '#7C3AED' : '#7C3AED33', color: editPreview ? '#fff' : '#7C3AED', fontWeight: 600,
                      }}>
                        {editPreview ? '✏️ Edit' : '👁 Preview'}
                      </button>
                      <button onClick={() => { clearJsonCache(`${COMPOSE_EDIT_CACHE_PREFIX}${editingId}`); setEditingId(null); setEditPreview(false); }} style={{
                        padding: '8px 16px', fontSize: 12, border: 'none', borderRadius: 6, cursor: 'pointer',
                        background: 'var(--border)', color: 'var(--muted)', fontWeight: 600,
                      }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );})}
        </div>
      )}

      {/* ── AI generation confirmation modal ────────────────────── */}
      {aiGenerationConfirm && (
        <div className="ai-confirm-modal-backdrop">
          <div className="ai-confirm-modal">
            <div className="ai-confirm-kicker">Review before spending AI credits</div>
            <div className="ai-confirm-title">Confirm dates &amp; audience before generation</div>
            <div className="ai-confirm-copy">
              Make sure ECC caught the right email timing and target audience. This does not send email — drafts still require review and approval.
            </div>

            <div className="ai-confirm-section date-focus">
              <span>Confirmed setup controls</span>
              <div className="ai-confirm-control-grid">
                <label>
                  <b>Campaign shape</b>
                  <select
                    value={aiGenerationConfirm.emailCount}
                    onChange={e => setAIGenerationConfirm(current => current ? {
                      ...current,
                      emailCount: Number(e.target.value),
                      campaign: campaignShapeLabel(Number(e.target.value)),
                    } : current)}
                  >
                    <option value={1}>1 broadcast + 1 trigger</option>
                    <option value={2}>2 broadcasts + triggers</option>
                    <option value={3}>3 broadcasts + triggers</option>
                    <option value={5}>5 broadcasts + triggers</option>
                  </select>
                </label>
                <label>
                  <b>Send timing / context</b>
                  <select
                    value={aiGenerationConfirm.timingMode || 'none'}
                    onChange={e => setAIGenerationConfirm(current => current ? {
                      ...current,
                      timingMode: e.target.value,
                      timingText: e.target.value === 'detected' ? (current.timingContexts?.[0]?.value || current.timingText || '') : current.timingText,
                    } : current)}
                  >
                    <option value="none">Schedule immediately</option>
                    {aiGenerationConfirm.timingContexts?.length > 0 && <option value="detected">Use detected time</option>}
                    <option value="custom">Choose time</option>
                  </select>
                </label>
              </div>
              {aiGenerationConfirm.timingMode !== 'none' && (
                <input
                  className="ai-confirm-text-input"
                  value={aiGenerationConfirm.timingText || ''}
                  onChange={e => setAIGenerationConfirm(current => current ? { ...current, timingText: e.target.value, timingMode: 'custom' } : current)}
                  placeholder="Example: Friday, May 8 at 4:00 PM ET"
                />
              )}
              {aiGenerationConfirm.timingContexts?.length ? (
                <div className="ai-confirm-timing-list compact">
                  {aiGenerationConfirm.timingContexts.map((ctx, idx) => (
                    <div key={`${ctx.label}-${idx}`} className="ai-confirm-timing-row">
                      <strong>{ctx.label}</strong>
                      <b>{ctx.value}</b>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="ai-confirm-warning">
                  Schedule immediately selected. ECC will ignore dates inside BODY copy like “Monday at noon” unless you choose a time above.
                </div>
              )}
            </div>

            <div className="ai-confirm-section audience-focus">
              <span>Estimated send audience</span>
              <strong>
                {aiGenerationConfirm.audienceLoading
                  ? 'Calculating audience…'
                  : aiGenerationConfirm.audienceError
                    ? 'Audience estimate unavailable'
                    : formatApproxCount(aiGenerationConfirm.audienceEstimate)}
              </strong>
              <p>
                Based on the selected lists and buyer-tier routing below. Final send count can change after bounces, unsubscribes, batch caps, and send-time filters.
              </p>
              {aiGenerationConfirm.audienceError && <em>{aiGenerationConfirm.audienceError}</em>}
            </div>

            <div className="ai-confirm-edit-grid">
              <div className="ai-confirm-edit-card">
                <span>Lists</span>
                <div className="ai-confirm-checkbox-grid">
                  {AI_SEGMENT_OPTIONS.map(({ value, label }) => {
                    const isFull = value === 'full_list';
                    const selectedSegments = aiGenerationConfirm.selectedSegments || [];
                    const checked = selectedSegments.includes(value);
                    const fullChecked = selectedSegments.includes('full_list');
                    return (
                      <label key={value} className={checked ? 'selected' : ''}>
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={!isFull && fullChecked}
                          onChange={e => {
                            let next;
                            if (isFull) {
                              next = e.target.checked ? ['full_list'] : [];
                            } else {
                              const current = selectedSegments.filter(v => v !== 'full_list' && v !== value);
                              next = e.target.checked ? [...current, value] : current;
                            }
                            if (next.length) updateAIGenerationSegments(next);
                          }}
                        />
                        {label}
                      </label>
                    );
                  })}
                </div>
              </div>
              <div className="ai-confirm-edit-card">
                <span>Trigger audience</span>
                <div className="ai-confirm-checkbox-grid">
                  {TIERS_ORDER.map(tier => {
                    const selectedBuyerLevels = aiGenerationConfirm.selectedBuyerLevels || [];
                    const checked = selectedBuyerLevels.includes(tier);
                    return (
                      <label key={tier} className={checked ? 'selected' : ''}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={e => {
                            const current = selectedBuyerLevels.filter(v => v !== tier);
                            updateAIGenerationBuyerLevels(e.target.checked ? [...current, tier] : current);
                          }}
                        />
                        {TIER_LABELS_AI[tier]}
                      </label>
                    );
                  })}
                </div>
                <small>Leave all unchecked to generate triggers for all buyer tiers.</small>
              </div>
            </div>

            <div className="ai-confirm-summary">
              <div>
                <span>Campaign</span>
                <strong>{aiGenerationConfirm.campaign}</strong>
              </div>
              <div>
                <span>Lists</span>
                <strong>{aiGenerationConfirm.lists}</strong>
              </div>
              <div>
                <span>Trigger audience</span>
                <strong>{aiGenerationConfirm.triggerAudience}</strong>
              </div>
              <div>
                <span>Images</span>
                <strong>{aiGenerationConfirm.images} attached</strong>
              </div>
            </div>

            <div className="ai-confirm-prompt">
              <span>Prompt preview</span>
              <p>{aiGenerationConfirm.prompt}</p>
            </div>

            <div className="ai-confirm-actions">
              <button
                type="button"
                onClick={() => setAIGenerationConfirm(null)}
                className="ai-confirm-secondary"
              >
                Edit Prompt
              </button>
              <button
                type="button"
                onClick={confirmAIGeneration}
                disabled={aiGenerating}
                className="ai-confirm-primary"
              >
                {aiGenerating ? 'Generating…' : 'Generate Campaign'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Approve confirmation modal ──────────────────────────── */}
      {approveWarning && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: 'var(--surface)',
            border: `2px solid ${approveWarning.hasNoTrigger ? '#F59E0B' : (approveWarning.campaignSchedule ? '#8B5CF6' : '#10B981')}`,
            borderRadius: 16, padding: 28, maxWidth: 440, width: '90%',
          }}>
            {approveWarning.hasNoTrigger ? (
              <>
                <div style={{ fontSize: 20, marginBottom: 8 }}>⚠️ No Trigger Email Linked</div>
                <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6, marginBottom: 20 }}>
                  This broadcast has <strong style={{ color: '#F59E0B' }}>no trigger email attached</strong>. If you approve and send,
                  clicks will <strong>not</strong> fire a follow-up sales email — you'll leave money on the table.<br /><br />
                  Are you sure you want to approve without a trigger?
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    onClick={async () => {
                      setApproveWarning(null);
                      try {
                        const approved = await post(`/api/drafts/${approveWarning.id}/approve`, {});
                        handleApproveComplete(approved);
                      } catch (err) { setMessage(`Error: ${err.message}`); }
                    }}
                    style={{ padding: '10px 20px', fontSize: 13, border: 'none', borderRadius: 8, cursor: 'pointer', background: '#F59E0B', color: '#000', fontWeight: 700 }}
                  >
                    Yes, approve anyway
                  </button>
                  <button
                    onClick={() => {
                      setApproveWarning(null);
                      const draft = drafts.find(d => d.id === approveWarning.id);
                      if (draft) handleEdit(draft);
                    }}
                    style={{ padding: '10px 20px', fontSize: 13, border: 'none', borderRadius: 8, cursor: 'pointer', background: 'var(--blue)', color: '#fff', fontWeight: 700 }}
                  >
                    Go back and link a trigger
                  </button>
                </div>
              </>
            ) : (
              <>
                <div style={{ fontSize: 20, marginBottom: 8 }}>
                  {approveWarning.campaignSchedule ? '🗓 Confirm Approve & Schedule' : '✅ Confirm Approve & Send Now'}
                </div>
                <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6, marginBottom: 20 }}>
                  {approveWarning.campaignSchedule ? (
                    <>
                      You're about to approve and <strong style={{ color: '#8B5CF6' }}>schedule</strong> this email:<br /><br />
                      <strong style={{ color: 'var(--text)' }}>{approveWarning.subject}</strong><br /><br />
                      <span style={{ color: '#8B5CF6', fontWeight: 700 }}>📅 Scheduled for: {approveWarning.campaignSchedule.label}</span><br /><br />
                      The send cron will pick it up at the scheduled time. Use the <strong>Schedule</strong> button instead if you want a different time.
                    </>
                  ) : (
                    <>
                      You're about to approve and <strong style={{ color: '#F59E0B' }}>send immediately</strong>:<br /><br />
                      <strong style={{ color: 'var(--text)' }}>{approveWarning.subject}</strong><br /><br />
                      The send cron will pick it up within 5 minutes. Use the <strong>Schedule</strong> button instead if you want to delay sending.
                    </>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    onClick={async () => {
                      setApproveWarning(null);
                      try {
                        const approved = await post(`/api/drafts/${approveWarning.id}/approve`, {});
                        handleApproveComplete(approved, approveWarning.campaignSchedule);
                      } catch (err) { setMessage(`Error: ${err.message}`); }
                    }}
                    style={{ padding: '10px 20px', fontSize: 13, border: 'none', borderRadius: 8, cursor: 'pointer', background: approveWarning.campaignSchedule ? '#8B5CF6' : '#10B981', color: '#fff', fontWeight: 700 }}
                  >
                    {approveWarning.campaignSchedule ? `Yes, approve & schedule` : 'Yes, approve & send now'}
                  </button>
                  <button
                    onClick={() => setApproveWarning(null)}
                    style={{ padding: '10px 20px', fontSize: 13, border: 'none', borderRadius: 8, cursor: 'pointer', background: 'var(--border)', color: 'var(--muted)', fontWeight: 700 }}
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Natural-language schedule modal ─────────────────────── */}
      {scheduleModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: 'var(--surface)', border: '2px solid #8B5CF6', borderRadius: 16,
            padding: 28, maxWidth: 520, width: '90%',
          }}>
            <div style={{ fontSize: 20, marginBottom: 8 }}>🗓 Schedule Email</div>
            <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6, marginBottom: 16 }}>
              Type it naturally. Examples: <strong>6 am tomorrow</strong>, <strong>6 am Tuesday</strong>, <strong>today 3:30pm</strong>, or <strong>in 2 hours</strong>.
              <br /><br />
              <strong style={{ color: 'var(--text)' }}>{scheduleModal.subject}</strong>
            </div>
            <label style={labelStyle}>Schedule for</label>
            <input
              autoFocus
              value={scheduleText}
              onChange={e => setScheduleText(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && schedulePreview && !scheduleSaving) handleScheduleConfirm();
              }}
              placeholder="6 am tomorrow"
              style={{ ...inputStyle, marginBottom: 10 }}
            />
            <div style={{
              minHeight: 36, padding: '9px 11px', borderRadius: 8,
              border: `1px solid ${scheduleError ? '#EF4444' : schedulePreview ? '#10B981' : 'var(--border)'}`,
              background: scheduleError ? '#EF444422' : schedulePreview ? '#10B98118' : 'var(--bg)',
              color: scheduleError ? '#FCA5A5' : schedulePreview ? '#10B981' : 'var(--muted)',
              fontSize: 13, marginBottom: 18,
            }}>
              {scheduleParsing && 'Parsing schedule…'}
              {!scheduleParsing && schedulePreview && `Schedules for ${schedulePreview.label}`}
              {!scheduleParsing && scheduleError && scheduleError}
              {!scheduleParsing && !schedulePreview && !scheduleError && 'Enter a schedule phrase to preview the exact send time.'}
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setScheduleModal(null)}
                style={{ padding: '10px 18px', fontSize: 13, border: 'none', borderRadius: 8, cursor: 'pointer', background: 'var(--border)', color: 'var(--muted)', fontWeight: 700 }}
              >
                Cancel
              </button>
              <button
                onClick={handleScheduleConfirm}
                disabled={!schedulePreview || scheduleSaving}
                style={{
                  padding: '10px 18px', fontSize: 13, border: 'none', borderRadius: 8,
                  cursor: (!schedulePreview || scheduleSaving) ? 'not-allowed' : 'pointer',
                  background: (!schedulePreview || scheduleSaving) ? '#374151' : '#8B5CF6',
                  color: '#fff', fontWeight: 700,
                }}
              >
                {scheduleSaving ? 'Scheduling…' : 'Approve & Schedule'}
              </button>
            </div>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 12 }}>
              Times are interpreted in Eastern Time. Scheduling approves this broadcast for future sending; triggered drafts are not scheduled by cron.
            </div>
          </div>
        </div>
      )}

      {/* ── Send Now confirmation modal ──────────────────────────── */}
      {sendNowModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: 'var(--surface)', border: '2px solid #F59E0B', borderRadius: 16,
            padding: 28, maxWidth: 480, width: '90%',
          }}>
            <div style={{ fontSize: 20, marginBottom: 8 }}>⚡ Confirm Send Now</div>
            <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6, marginBottom: 16 }}>
              This sends <strong style={{ color: '#F59E0B' }}>immediately</strong> — it does not wait for the 5-minute cron.
            </div>
            <div style={{
              padding: '12px 14px', borderRadius: 10, background: 'var(--bg)',
              border: '1px solid var(--border)', marginBottom: 18,
            }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>
                {sendNowModal.subject || 'No subject'}
              </div>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 4 }}>
                Segment: <strong style={{ color: 'var(--text)' }}>{sendNowModal.segmentLabel}</strong>
              </div>
              {sendNowModal.directRecipients?.length > 0 ? (
                <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>
                  Only these addresses ({sendNowModal.directRecipients.length}):
                  <div style={{ color: 'var(--text)', fontFamily: "'JetBrains Mono', monospace", marginTop: 4, wordBreak: 'break-all' }}>
                    {sendNowModal.directRecipients.join(', ')}
                  </div>
                </div>
              ) : (
                <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>
                  Buyer tiers: <strong style={{ color: 'var(--text)' }}>{sendNowModal.buyerLevelLabel}</strong>
                </div>
              )}
              {sendNowModal.excludedRecipients?.length > 0 && (
                <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>
                  Leaving out ({sendNowModal.excludedRecipients.length}):
                  <div style={{ color: 'var(--text)', fontFamily: "'JetBrains Mono', monospace", marginTop: 4, wordBreak: 'break-all' }}>
                    {sendNowModal.excludedRecipients.join(', ')}
                  </div>
                </div>
              )}
              <div style={{
                fontSize: 13, fontWeight: 700,
                color: sendNowModal.countError ? '#EF4444' : '#F59E0B',
              }}>
                {sendNowModal.countLoading && 'Estimating recipients…'}
                {!sendNowModal.countLoading && sendNowModal.countError && `Could not estimate: ${sendNowModal.countError}`}
                {!sendNowModal.countLoading && !sendNowModal.countError && `~${Number(sendNowModal.count || 0).toLocaleString()} projected recipients`}
              </div>
            </div>
            {sendNowModal.sendError && (
              <div style={{
                padding: '9px 11px', borderRadius: 8, border: '1px solid #EF4444',
                background: '#EF444422', color: '#FCA5A5', fontSize: 13, marginBottom: 16,
              }}>
                {sendNowModal.sendError}
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setSendNowModal(null)}
                disabled={sendNowInFlight}
                style={{ padding: '10px 18px', fontSize: 13, border: 'none', borderRadius: 8, cursor: sendNowInFlight ? 'not-allowed' : 'pointer', background: 'var(--border)', color: 'var(--muted)', fontWeight: 700 }}
              >
                Cancel
              </button>
              <button
                onClick={handleSendNowConfirm}
                disabled={sendNowInFlight}
                style={{
                  padding: '10px 18px', fontSize: 13, border: 'none', borderRadius: 8,
                  cursor: sendNowInFlight ? 'not-allowed' : 'pointer',
                  background: sendNowInFlight ? '#374151' : '#F59E0B',
                  color: sendNowInFlight ? '#fff' : '#000', fontWeight: 700,
                }}
              >
                {sendNowInFlight ? 'Sending…' : 'Yes, send now'}
              </button>
            </div>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 12 }}>
              Uses the same safety guards as the automatic sender: duplicate-send lock, test-address exclusion, and existing member/buyer-tier exclusions.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const btnStyle = (color) => ({
  padding: '6px 12px', fontSize: 11, border: 'none', borderRadius: 6, cursor: 'pointer',
  background: `${color}22`, color, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace",
});
